import React, { useState, useEffect, useCallback, useRef } from 'react';
import { alpacaService } from '../../services/AlpacaService';
import type {
  StrategistConfig,
  StrategistAction,
  StrategistDecision,
  GateCandidate,
  StrategistGateReview,
  StrategistRun,
  StrategistReplay,
  StrategistRule,
  StrategistStats,
  WeeklyReview,
  PostMortemAnalysis,
} from '../../services/AlpacaService';
import type { BacktestResult, OptimizationResult } from '../../services/AlpacaService';
import AgentPipeline from '../../components/AgentPipeline/AgentPipeline';
import TradeTimeline from '../../components/TradeTimeline/TradeTimeline';
import PortfolioHeatmap from '../../components/PortfolioHeatmap/PortfolioHeatmap';
import SectorSentiment from '../../components/SectorSentiment/SectorSentiment';
import type { SectorSentimentResponse } from '../../services/AlpacaService';
import ArchitectureFlow from '../../components/ArchitectureFlow/ArchitectureFlow';
import TradeCalendar from '../../components/TradeCalendar/TradeCalendar';
import type { CalendarResponse } from '../../components/TradeCalendar/calendarUtils';
import { useToast } from '../../context/ToastContext';
import { useWebSocket } from '../../context/WebSocketContext';
import styles from './Strategist.module.css';

/* ── Debounce helper ── */
function useDebounce() {
  const pending = useRef<Set<string>>(new Set());
  const debounced = useCallback(async (key: string, fn: () => Promise<void>) => {
    if (pending.current.has(key)) return;
    pending.current.add(key);
    try {
      await fn();
    } finally {
      pending.current.delete(key);
    }
  }, []);
  return debounced;
}

/* ── Skeleton Loader ── */
const StrategistSkeleton: React.FC = () => (
  <div className={styles.page}>
    <div className={`${styles.skeleton} ${styles.skeletonPulse}`}>
      <div className={styles.skeletonHeader}>
        <div>
          <div className={styles.skeletonLine} style={{ width: 220, height: 28, marginBottom: 8 }} />
          <div className={styles.skeletonLine} style={{ width: 340, height: 14 }} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className={styles.skeletonLine} style={{ width: 80, height: 36, borderRadius: 8 }} />
          <div className={styles.skeletonLine} style={{ width: 100, height: 36, borderRadius: 8 }} />
        </div>
      </div>
      <div className={styles.skeletonLine} style={{ width: '100%', height: 1 }} />
      <div className={`${styles.skeletonBlock}`} style={{ height: 60, padding: 14 }}>
        <div className={styles.skeletonLine} style={{ width: '80%', height: 14 }} />
      </div>
      <div className={styles.skeletonGrid}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`${styles.skeletonCard}`} />
        ))}
      </div>
      <div className={`${styles.skeletonBlock}`} style={{ height: 160, padding: 20 }}>
        <div className={styles.skeletonLine} style={{ width: 140, height: 18, marginBottom: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className={styles.skeletonLine} style={{ height: 36, borderRadius: 6 }} />
          <div className={styles.skeletonLine} style={{ height: 36, borderRadius: 6 }} />
          <div className={styles.skeletonLine} style={{ height: 36, borderRadius: 6 }} />
          <div className={styles.skeletonLine} style={{ height: 36, borderRadius: 6 }} />
        </div>
      </div>
    </div>
  </div>
);

type Tab =
  | 'overview'
  | 'inbox'
  | 'actions'
  | 'journal'
  | 'replay'
  | 'gates'
  | 'sectors'
  | 'heatmap'
  | 'weekly'
  | 'monthly'
  | 'postmortem'
  | 'automation'
  | 'backtest'
  | 'pipeline'
  | 'architecture'
  | 'calendar';

const CORE_TABS: Tab[] = [
  'overview',
  'inbox',
  'journal',
  'calendar',
  'replay',
  'gates',
  'postmortem',
  'weekly',
  'heatmap',
];
const ADVANCED_TABS: Tab[] = ['automation', 'actions', 'sectors', 'monthly', 'backtest', 'pipeline', 'architecture'];
const TAB_LABELS: Record<Tab, string> = {
  overview: 'Dashboard',
  inbox: 'Inbox',
  actions: 'Action Queue',
  journal: 'Journal',
  replay: 'Decision Replay',
  gates: 'Skipped Trades',
  sectors: 'Sectors',
  heatmap: 'Exposure',
  weekly: 'Weekly Report',
  monthly: 'Monthly Review',
  postmortem: 'Lessons',
  automation: 'Automation',
  backtest: 'Backtest',
  pipeline: 'Agent Pipeline',
  architecture: 'How It Works',
  calendar: 'Calendar',
};

type TabFocus = {
  tab: Tab;
  key: string;
};

type StrategyRuleStatus = 'pending' | 'active' | 'retired';
type StrategyRuleSource = 'Inbox' | 'Replay' | 'Post-Mortem';

type StrategyRuleCandidate = StrategistRule;

const STRATEGY_RULES_STORAGE_KEY = 'signaldeck.strategyRules.v1';

const isStrategyRuleCandidate = (rule: unknown): rule is StrategyRuleCandidate => {
  if (!rule || typeof rule !== 'object') return false;
  const candidate = rule as Partial<StrategyRuleCandidate>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.text === 'string' &&
    typeof candidate.source === 'string' &&
    typeof candidate.sourceTitle === 'string' &&
    typeof candidate.createdAt === 'string' &&
    ['pending', 'active', 'retired'].includes(String(candidate.status))
  );
};

const readStrategyRuleCandidates = (): StrategyRuleCandidate[] => {
  try {
    const raw = window.localStorage.getItem(STRATEGY_RULES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isStrategyRuleCandidate);
  } catch {
    return [];
  }
};

function timeAgo(iso: string): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'pending':
      return styles.badgePending;
    case 'awaiting_approval':
      return styles.badgeApproval;
    case 'executed':
      return styles.badgeExecuted;
    case 'rejected':
      return styles.badgeRejected;
    case 'failed':
      return styles.badgeRejected;
    case 'expired':
      return styles.badgeRejected;
    case 'open':
      return styles.badgeOpen;
    case 'closed_profit':
      return styles.badgeProfit;
    case 'closed_loss':
      return styles.badgeLoss;
    default:
      return '';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'awaiting_approval':
      return 'Awaiting';
    case 'closed_profit':
      return 'Profit';
    case 'closed_loss':
      return 'Loss';
    case 'failed':
      return 'Failed';
    case 'expired':
      return 'Expired';
    default:
      return status;
  }
}

function urgencyClass(urgency: string): string {
  switch (urgency) {
    case 'immediate':
      return styles.urgImmediate;
    case 'today':
      return styles.urgToday;
    default:
      return styles.urgWeek;
  }
}

function confColor(conf: number): string {
  if (conf >= 75) return styles.confHigh;
  if (conf >= 50) return styles.confMed;
  return styles.confLow;
}

function biasClass(adj: string): string {
  switch (adj) {
    case 'increase':
      return styles.biasIncrease;
    case 'decrease':
      return styles.biasDecrease;
    default:
      return styles.biasMaintain;
  }
}

const Strategist: React.FC = () => {
  const [tab, setTab] = useState<Tab>('overview');
  const [tabFocus, setTabFocus] = useState<TabFocus | null>(null);
  const [showAdvancedTabs, setShowAdvancedTabs] = useState(false);
  const [config, setConfig] = useState<StrategistConfig | null>(null);
  const [actions, setActions] = useState<StrategistAction[]>([]);
  const [decisions, setDecisions] = useState<StrategistDecision[]>([]);
  const [runs, setRuns] = useState<StrategistRun[]>([]);
  const [stats, setStats] = useState<StrategistStats | null>(null);
  const [reviews, setReviews] = useState<WeeklyReview[]>([]);
  const [monthlyReviews, setMonthlyReviews] = useState<any[]>([]);
  const [heatmapData, setHeatmapData] = useState<any>(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [sectorsData, setSectorsData] = useState<SectorSentimentResponse | null>(null);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [postmortems, setPostmortems] = useState<PostMortemAnalysis[]>([]);
  const [ruleCandidates, setRuleCandidates] = useState<StrategyRuleCandidate[]>(() => readStrategyRuleCandidates());
  const [gateReview, setGateReview] = useState<StrategistGateReview | null>(null);
  const [gateLoading, setGateLoading] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateRefreshedAt, setGateRefreshedAt] = useState<string | null>(null);
  const [gateFollowupLoading, setGateFollowupLoading] = useState(false);
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState<OptimizationResult | null>(null);
  const [optimizeLoading, setOptimizeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [liveChecked, setLiveChecked] = useState(false);
  const [calendarData, setCalendarData] = useState<CalendarResponse | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const debounce = useDebounce();
  const { addToast } = useToast();
  const advancedOpen = showAdvancedTabs || ADVANCED_TABS.includes(tab);

  useEffect(() => {
    try {
      window.localStorage.setItem(STRATEGY_RULES_STORAGE_KEY, JSON.stringify(ruleCandidates));
    } catch {
      // Local rule drafts are convenience state; ignore storage failures.
    }
  }, [ruleCandidates]);

  const handleNavigate = useCallback((nextTab: Tab, focusKey?: string) => {
    setTab(nextTab);
    setTabFocus(focusKey ? { tab: nextTab, key: focusKey } : null);
  }, []);

  const loadRuleCandidates = useCallback(async () => {
    try {
      const resp = await alpacaService.getStrategistRules();
      setRuleCandidates(Array.isArray(resp.rules) ? resp.rules.filter(isStrategyRuleCandidate) : []);
    } catch (err) {
      console.error('Failed to load strategist rules:', err);
    }
  }, []);

  useEffect(() => {
    loadRuleCandidates();
  }, [loadRuleCandidates]);

  const handlePromoteRule = useCallback(
    async (draft: { text: string; source: StrategyRuleSource; sourceTitle: string }) => {
      const text = draft.text.trim();
      if (!text) {
        addToast('Write a takeaway before promoting it to a rule', 'error');
        return;
      }
      if (ruleCandidates.some((rule) => rule.text.toLowerCase() === text.toLowerCase())) {
        addToast('That rule candidate already exists', 'error');
        return;
      }
      try {
        const resp = await alpacaService.createStrategistRule({
          text,
          source: draft.source,
          sourceTitle: draft.sourceTitle,
        });
        const createdRule = resp?.rule;
        if (!isStrategyRuleCandidate(createdRule)) {
          throw new Error('Rule candidate response was incomplete');
        }
        setRuleCandidates((current) => [
          createdRule,
          ...current.filter((rule) => isStrategyRuleCandidate(rule) && rule.id !== createdRule.id),
        ]);
        addToast('Rule candidate added to Lessons', 'success');
      } catch (err: any) {
        addToast(err.message || 'Failed to save rule candidate', 'error');
      }
    },
    [addToast, ruleCandidates],
  );

  const handleRuleStatusChange = useCallback(
    async (id: string, status: StrategyRuleStatus) => {
      const previous = ruleCandidates;
      setRuleCandidates((current) => current.map((rule) => (rule.id === id ? { ...rule, status } : rule)));
      try {
        const resp = await alpacaService.updateStrategistRule(id, { status });
        if (resp.rule) {
          setRuleCandidates((current) => current.map((rule) => (rule.id === id ? resp.rule : rule)));
        }
      } catch (err: any) {
        setRuleCandidates(previous);
        addToast(err.message || 'Failed to update rule status', 'error');
      }
    },
    [addToast, ruleCandidates],
  );

  const loadOverview = useCallback(async () => {
    try {
      const [cfg, st] = await Promise.all([alpacaService.getStrategistConfig(), alpacaService.getStrategistStats()]);
      setConfig(cfg);
      setStats(st);
    } catch (err) {
      console.error('Failed to load strategist overview:', err);
    }
  }, []);

  const loadActions = useCallback(async () => {
    try {
      const resp = await alpacaService.getStrategistActions();
      setActions(resp.actions);
    } catch (err) {
      console.error('Failed to load actions:', err);
    }
  }, []);

  const loadDecisions = useCallback(async () => {
    try {
      const resp = await alpacaService.getStrategistDecisions(undefined, 100);
      setDecisions(resp.decisions);
    } catch (err) {
      console.error('Failed to load decisions:', err);
    }
  }, []);

  const loadWeekly = useCallback(async () => {
    try {
      const [rev, rn] = await Promise.all([alpacaService.getStrategistWeekly(), alpacaService.getStrategistRuns(10)]);
      setReviews(rev.reviews);
      setRuns(rn.runs);
    } catch (err) {
      console.error('Failed to load weekly:', err);
    }
  }, []);

  const loadHeatmap = useCallback(async () => {
    setHeatmapLoading(true);
    try {
      const resp = await alpacaService.getPortfolioHeatmap();
      setHeatmapData(resp);
    } catch (err) {
      console.error('Failed to load heatmap:', err);
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  const loadSectors = useCallback(async () => {
    setSectorsLoading(true);
    try {
      const resp = await alpacaService.getStrategistSectors();
      setSectorsData(resp);
    } catch (err) {
      console.error('Failed to load sectors:', err);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  const loadMonthly = useCallback(async () => {
    try {
      const resp = await alpacaService.getStrategistMonthly();
      setMonthlyReviews(resp.reviews);
    } catch (err) {
      console.error('Failed to load monthly reviews:', err);
    }
  }, []);

  const loadPostmortems = useCallback(async () => {
    try {
      const resp = await alpacaService.getStrategistPostmortems();
      setPostmortems(resp.postmortems);
    } catch (err) {
      console.error('Failed to load postmortems:', err);
    }
  }, []);

  const loadGateReview = useCallback(async () => {
    setGateLoading(true);
    setGateError(null);
    try {
      const resp = await alpacaService.getStrategistGateReview({ limit: 20 });
      setGateReview(resp);
      setGateRefreshedAt(new Date().toISOString());
    } catch (err: any) {
      console.error('Failed to load gate review:', err);
      const message = err.message || 'Failed to load gate review';
      setGateError(
        message.toLowerCase().includes('unauthorized') ? 'Sign in to review strategist gate history.' : message,
      );
    } finally {
      setGateLoading(false);
    }
  }, []);

  const updateGateFollowups = useCallback(async () => {
    setGateFollowupLoading(true);
    try {
      const resp = await alpacaService.updateStrategistGateFollowups();
      addToast(
        `Updated ${resp.outcomesUpdated} rejected trade outcome${resp.outcomesUpdated === 1 ? '' : 's'}`,
        'success',
      );
      await loadGateReview();
    } catch (err: any) {
      addToast(err.message || 'Failed to update rejected trade outcomes', 'error');
    } finally {
      setGateFollowupLoading(false);
    }
  }, [addToast, loadGateReview]);

  const loadCalendar = useCallback(async () => {
    if (calendarData) return; // Use cached data per D-08
    setCalendarLoading(true);
    setCalendarError(null);
    try {
      const data = await alpacaService.getCalendarData(0);
      if (!data?.days || !Array.isArray(data.days)) {
        setCalendarError('Invalid calendar response from server');
        return;
      }
      setCalendarData(data);
    } catch (err: any) {
      setCalendarError(err.message || 'Failed to load calendar data');
    } finally {
      setCalendarLoading(false);
    }
  }, [calendarData]);

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      await loadOverview();
      setLoading(false);
    };
    load();
  }, [loadOverview]);

  useEffect(() => {
    if (tab === 'overview') {
      loadDecisions();
      loadPostmortems();
      loadWeekly();
      loadGateReview();
    }
    if (tab === 'inbox') {
      loadDecisions();
      loadPostmortems();
      loadWeekly();
      loadGateReview();
    }
    if (tab === 'actions') loadActions();
    if (tab === 'journal') loadDecisions();
    if (tab === 'replay') {
      loadDecisions();
      loadPostmortems();
    }
    if (tab === 'gates') loadGateReview();
    if (tab === 'sectors') loadSectors();
    if (tab === 'heatmap') loadHeatmap();
    if (tab === 'weekly') loadWeekly();
    if (tab === 'monthly') loadMonthly();
    if (tab === 'postmortem') loadPostmortems();
    if (tab === 'calendar') loadCalendar();
  }, [
    tab,
    loadActions,
    loadDecisions,
    loadSectors,
    loadHeatmap,
    loadWeekly,
    loadMonthly,
    loadPostmortems,
    loadGateReview,
    loadCalendar,
  ]);

  // WebSocket: instant refresh when strategist creates/executes actions
  const { on } = useWebSocket();
  useEffect(() => {
    const unsub = on('action_update', (data: any) => {
      // Don't reload config on self-triggered config saves
      if (data?.config_updated) return;
      loadActions();
    });
    return unsub;
  }, [on, loadActions]);

  const handleToggle = async (enabled: boolean) => {
    if (!config) return;
    try {
      await alpacaService.saveStrategistConfig({ enabled });
      setConfig({ ...config, enabled });
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  const handleConfigChange = async (key: keyof StrategistConfig, value: any) => {
    if (!config) return;
    try {
      await alpacaService.saveStrategistConfig({ [key]: value });
      setConfig({ ...config, [key]: value });
    } catch (err) {
      console.error('Failed to save config:', err);
    }
  };

  const handleApprove = (actionSK: string) => {
    debounce(`approve-${actionSK}`, async () => {
      setActionLoading(actionSK);
      try {
        await alpacaService.approveStrategistAction(actionSK);
        addToast('Action approved and executed', 'success');
        await loadActions();
      } catch (err: any) {
        addToast(err.message || 'Failed to approve action', 'error');
      }
      setActionLoading(null);
    });
  };

  const handleReject = (actionSK: string) => {
    debounce(`reject-${actionSK}`, async () => {
      setActionLoading(actionSK);
      try {
        await alpacaService.rejectStrategistAction(actionSK);
        addToast('Action rejected', 'info');
        await loadActions();
      } catch (err: any) {
        addToast(err.message || 'Failed to reject action', 'error');
      }
      setActionLoading(null);
    });
  };

  const handleTriggerRun = () => {
    debounce('trigger-run', async () => {
      setTriggerLoading(true);
      addToast('Strategist run kicked off — 14 agents scanning...', 'info');
      try {
        const resp = await alpacaService.triggerStrategistRun();
        if (resp.success) {
          addToast('Pipeline running! Results in ~30s. Refreshing...', 'success');
          // Poll for results after a delay
          setTimeout(async () => {
            await loadOverview();
            if (tab === 'actions') await loadActions();
            if (tab === 'journal') await loadDecisions();
            addToast('Strategist run complete — check Action Queue for results', 'success');
            setTriggerLoading(false);
          }, 35000);
        } else {
          addToast(resp.result?.reason || 'Run started but no confirmation received', 'info');
          setTriggerLoading(false);
        }
      } catch (err: any) {
        addToast(err.message || 'Failed to trigger strategist run', 'error');
        setTriggerLoading(false);
      }
    });
  };

  if (loading) {
    return <StrategistSkeleton />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header} data-tour="strategist-header">
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Trade Review</h1>
          <p className={styles.subtitle}>Decision journal, skipped trades, lessons, and weekly process review</p>
        </div>
        <div className={styles.headerActions}>
          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              checked={config?.enabled ?? false}
              onChange={(e) => handleToggle(e.target.checked)}
            />
            {config?.enabled ? 'Active' : 'Inactive'}
          </label>
          <div className={styles.modeToggle} data-tour="mode-toggle">
            <button
              className={`${styles.modeBtn} ${config?.mode !== 'live' ? styles.modeBtnActive : ''}`}
              onClick={() => {
                if (config?.mode === 'live') {
                  handleConfigChange('mode', 'paper');
                }
              }}
            >
              Paper
            </button>
            <button
              className={`${styles.modeBtn} ${styles.modeBtnLive} ${config?.mode === 'live' ? styles.modeBtnLiveActive : ''}`}
              onClick={() => {
                if (config?.mode !== 'live') {
                  setShowLiveConfirm(true);
                  setLiveChecked(false);
                }
              }}
            >
              Live
            </button>
          </div>
          <button
            className={`${styles.btnDeploy} ${triggerLoading ? styles.btnRunning : ''}`}
            data-tour="btn-run-now"
            onClick={handleTriggerRun}
            disabled={triggerLoading}
          >
            {triggerLoading ? '⏳ Running 14 agents...' : 'Run Now'}
          </button>
        </div>
      </div>

      <div className={styles.tabGroups}>
        <div className={styles.tabGroup}>
          <span className={styles.tabGroupLabel}>Trade Review</span>
          <div className={styles.tabs}>
            {CORE_TABS.map((t) => (
              <button
                key={t}
                className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
                onClick={() => handleNavigate(t)}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.tabGroup}>
          <div className={styles.advancedTabHeader}>
            <span className={styles.tabGroupLabel}>Advanced</span>
            <button
              type="button"
              className={styles.advancedToggle}
              onClick={() => setShowAdvancedTabs((current) => !current)}
            >
              {advancedOpen ? 'Hide Advanced' : 'Show Advanced'}
            </button>
          </div>
          {advancedOpen && (
            <div className={styles.tabs}>
              {ADVANCED_TABS.map((t) => (
                <button
                  key={t}
                  className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
                  onClick={() => handleNavigate(t)}
                >
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>
          )}
          {!advancedOpen && (
            <div className={styles.advancedHint}>
              Automation settings, research views, backtests, and pipeline details live here.
            </div>
          )}
        </div>
      </div>

      {tab === 'overview' && (
        <OverviewTab
          stats={stats}
          decisions={decisions}
          gateReview={gateReview}
          gateLoading={gateLoading}
          postmortems={postmortems}
          reviews={reviews}
          runs={runs}
          onNavigate={handleNavigate}
        />
      )}
      {tab === 'inbox' && (
        <ReviewInboxTab
          decisions={decisions}
          gateReview={gateReview}
          gateLoading={gateLoading}
          postmortems={postmortems}
          reviews={reviews}
          runs={runs}
          onNavigate={handleNavigate}
          onPromoteRule={handlePromoteRule}
        />
      )}
      {tab === 'automation' && <AutomationTab config={config} onConfigChange={handleConfigChange} />}
      {tab === 'actions' && (
        <ActionsTab actions={actions} onApprove={handleApprove} onReject={handleReject} actionLoading={actionLoading} />
      )}
      {tab === 'journal' && <JournalTab decisions={decisions} />}
      {tab === 'replay' && (
        <TradeReplayTab
          decisions={decisions}
          postmortems={postmortems}
          focusedSK={tabFocus?.tab === 'replay' ? tabFocus.key : null}
          onPromoteRule={handlePromoteRule}
        />
      )}
      {tab === 'gates' && (
        <GateReviewTab
          data={gateReview}
          loading={gateLoading}
          error={gateError}
          refreshedAt={gateRefreshedAt}
          onRefresh={loadGateReview}
          onUpdateFollowups={updateGateFollowups}
          followupLoading={gateFollowupLoading}
          focusedKey={tabFocus?.tab === 'gates' ? tabFocus.key : null}
        />
      )}
      {tab === 'sectors' && (
        <SectorSentiment
          sectors={sectorsData?.sectors ?? []}
          updatedAt={sectorsData?.updatedAt ?? null}
          loading={sectorsLoading}
        />
      )}
      {tab === 'heatmap' &&
        (heatmapLoading ? (
          <div className={styles.emptyState}>
            <p>Loading portfolio heatmap...</p>
          </div>
        ) : heatmapData && heatmapData.symbols?.length > 0 ? (
          <PortfolioHeatmap data={heatmapData} />
        ) : (
          <div className={styles.emptyState}>
            <p>No open positions</p>
            <p style={{ fontSize: '0.85rem' }}>The portfolio heatmap shows correlation between your open positions.</p>
          </div>
        ))}
      {tab === 'weekly' && <WeeklyTab reviews={reviews} runs={runs} />}
      {tab === 'monthly' && <MonthlyTab reviews={monthlyReviews} />}
      {tab === 'postmortem' && (
        <PostmortemTab
          postmortems={postmortems}
          decisions={decisions}
          focusedSK={tabFocus?.tab === 'postmortem' ? tabFocus.key : null}
          ruleCandidates={ruleCandidates}
          onPromoteRule={handlePromoteRule}
          onRuleStatusChange={handleRuleStatusChange}
        />
      )}
      {tab === 'backtest' && (
        <BacktestTab
          result={backtestResult}
          loading={backtestLoading}
          onRun={async (days) => {
            setBacktestLoading(true);
            try {
              const result = await alpacaService.runPortfolioBacktest({ days, initialCapital: 100000 });
              setBacktestResult(result);
            } catch (err: any) {
              addToast(err.message || 'Backtest failed', 'error');
            } finally {
              setBacktestLoading(false);
            }
          }}
          optimizeResult={optimizeResult}
          optimizeLoading={optimizeLoading}
          onOptimize={async (days) => {
            setOptimizeLoading(true);
            try {
              const result = await alpacaService.runOptimization({ days, initialCapital: 100000 });
              setOptimizeResult(result);
            } catch (err: any) {
              addToast(err.message || 'Optimization failed', 'error');
            } finally {
              setOptimizeLoading(false);
            }
          }}
          liveStats={stats}
        />
      )}
      {tab === 'pipeline' && stats && (
        <AgentPipeline
          breakdown={stats.lastAgentBreakdown}
          isRunning={triggerLoading}
          regime={stats.lastMarketAssessment?.match(/\[(\w+)\]/)?.[1] ?? null}
        />
      )}
      {tab === 'architecture' && <ArchitectureFlow />}
      {tab === 'calendar' && (
        <TradeCalendar
          data={calendarData}
          loading={calendarLoading}
          error={calendarError}
          onReplayTrade={(decisionSK) => handleNavigate('replay', decisionSK)}
          onRetry={() => {
            setCalendarData(null);
            setTimeout(() => loadCalendar(), 0);
          }}
        />
      )}

      {/* ── Live Mode Confirmation Modal ── */}
      {showLiveConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowLiveConfirm(false)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Switch to Live Mode?</h3>
            <p className={styles.modalText}>
              Claude will trade with <strong>real money</strong> on your live Alpaca account. Depending on your autonomy
              level, trades may execute automatically.
            </p>
            <div className={styles.modalAutonomy}>
              Current autonomy:{' '}
              <strong>
                {config?.autonomyLevel === 'full_auto'
                  ? 'Full Auto'
                  : config?.autonomyLevel === 'semi_auto'
                    ? 'Semi-Auto'
                    : 'Manual'}
              </strong>
              {config?.autonomyLevel === 'full_auto' && (
                <span className={styles.modalWarnInline}> — Claude will auto-execute ALL trades!</span>
              )}
              {config?.autonomyLevel === 'semi_auto' && (
                <span> — High-confidence urgent trades auto-execute, others need approval</span>
              )}
              {config?.autonomyLevel === 'manual' && <span> — All trades require your approval</span>}
            </div>
            <div className={styles.liveWarning}>
              ⚠️ This will use <strong>REAL MONEY</strong> on your live Alpaca account!
            </div>
            <label className={styles.liveCheckbox}>
              <input type="checkbox" checked={liveChecked} onChange={(e) => setLiveChecked(e.target.checked)} />I
              understand Claude will trade with real money
            </label>
            <div className={styles.modalBtns}>
              <button className={styles.modalCancel} onClick={() => setShowLiveConfirm(false)}>
                Cancel
              </button>
              <button
                className={styles.modalConfirm}
                disabled={!liveChecked}
                onClick={() => {
                  handleConfigChange('mode', 'live');
                  setShowLiveConfirm(false);
                }}
              >
                Switch to Live
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Overview Tab                                                  */
/* ══════════════════════════════════════════════════════════════ */

const OverviewTab: React.FC<{
  stats: StrategistStats | null;
  decisions: StrategistDecision[];
  gateReview: StrategistGateReview | null;
  gateLoading: boolean;
  postmortems: PostMortemAnalysis[];
  reviews: WeeklyReview[];
  runs: StrategistRun[];
  onNavigate: (tab: Tab, focusKey?: string) => void;
}> = ({ stats, decisions, gateReview, gateLoading, postmortems, reviews, runs, onNavigate }) => {
  const startSteps: Array<{ label: string; detail: string; tab: Tab }> = [
    {
      label: 'Check the month',
      detail: 'Scan daily P&L and open the trades behind the red and green days.',
      tab: 'calendar',
    },
    {
      label: 'Replay one decision',
      detail: 'Compare the original thesis against what actually happened after entry.',
      tab: 'replay',
    },
    {
      label: 'Review skipped trades',
      detail: 'See which ideas were blocked and whether the filters are doing useful work.',
      tab: 'gates',
    },
    {
      label: 'Read the weekly report',
      detail: 'Use the latest review to spot patterns worth keeping or cutting.',
      tab: 'weekly',
    },
  ];

  const startPanel = (
    <section className={styles.startPanel} aria-labelledby="trade-review-start">
      <div className={styles.startHeader}>
        <div>
          <h2 id="trade-review-start">Start Here</h2>
          <p>Work through the highest-signal review loop before touching automation or research views.</p>
        </div>
        <button type="button" className={styles.startPrimary} onClick={() => onNavigate('inbox')}>
          Open Inbox
        </button>
      </div>
      <div className={styles.startSteps}>
        {startSteps.map((step) => (
          <button key={step.tab} type="button" className={styles.startStep} onClick={() => onNavigate(step.tab)}>
            <span className={styles.startStepLabel}>{step.label}</span>
            <span className={styles.startStepDetail}>{step.detail}</span>
          </button>
        ))}
      </div>
    </section>
  );

  const inboxSummary = (
    <DashboardInboxSummary
      decisions={decisions}
      gateReview={gateReview}
      gateLoading={gateLoading}
      postmortems={postmortems}
      reviews={reviews}
      runs={runs}
      onNavigate={onNavigate}
    />
  );

  if (!stats) {
    return (
      <>
        {startPanel}
        {inboxSummary}
      </>
    );
  }

  return (
    <>
      {startPanel}
      {inboxSummary}

      {stats.lastMarketAssessment && (
        <div className={styles.brainBanner}>
          <span className={styles.brainIcon}>🧠</span>
          <div className={styles.brainText}>
            <strong>Claude's Market Read:</strong> {stats.lastMarketAssessment}
            {stats.lastRunAt && <span className={styles.assessmentTime}> — {timeAgo(stats.lastRunAt)}</span>}
          </div>
        </div>
      )}

      {stats.readinessScore != null && (
        <div className={`${styles.readinessCard} ${styles[`readiness_${stats.readinessLevel}`]}`}>
          <div className={styles.readinessHeader}>
            <div className={styles.readinessScoreWrap}>
              <svg className={styles.readinessRing} viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="var(--border-secondary)" strokeWidth="8" />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(stats.readinessScore / 100) * 327} 327`}
                  strokeDashoffset="0"
                  transform="rotate(-90 60 60)"
                  className={styles.readinessRingFill}
                />
                <text x="60" y="55" textAnchor="middle" className={styles.readinessScoreText}>
                  {stats.readinessScore}
                </text>
                <text x="60" y="72" textAnchor="middle" className={styles.readinessScoreLabel}>
                  / 100
                </text>
              </svg>
            </div>
            <div className={styles.readinessDetails}>
              <div className={styles.readinessTitle}>
                Paper-to-Live Readiness
                <span className={`${styles.readinessBadge} ${styles[`readinessBadge_${stats.readinessLevel}`]}`}>
                  {stats.readinessLevel === 'not_ready'
                    ? 'Not Ready'
                    : stats.readinessLevel === 'developing'
                      ? 'Developing'
                      : stats.readinessLevel === 'almost_ready'
                        ? 'Almost Ready'
                        : 'Ready'}
                </span>
              </div>
              {stats.readinessBreakdown && (
                <div className={styles.readinessBars}>
                  {[
                    { label: 'Sample Size', value: stats.readinessBreakdown.sampleSize, max: 25 },
                    { label: 'Win Rate', value: stats.readinessBreakdown.winRate, max: 25 },
                    { label: 'Profitability', value: stats.readinessBreakdown.profitability, max: 25 },
                    { label: 'Consistency', value: stats.readinessBreakdown.consistency, max: 25 },
                  ].map((b) => (
                    <div key={b.label} className={styles.readinessBarRow}>
                      <span className={styles.readinessBarLabel}>{b.label}</span>
                      <div className={styles.readinessBarTrack}>
                        <div className={styles.readinessBarFill} style={{ width: `${(b.value / b.max) * 100}%` }} />
                      </div>
                      <span className={styles.readinessBarValue}>
                        {b.value}/{b.max}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {stats.readinessAdvice && stats.readinessAdvice.length > 0 && (
                <div className={styles.readinessAdvice}>
                  {stats.readinessAdvice.map((a, i) => (
                    <div key={i} className={styles.readinessAdviceItem}>
                      {a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Win Rate</div>
          <div className={styles.statValue}>{stats.winRate}%</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Avg P&L</div>
          <div className={`${styles.statValue} ${stats.avgPnlPct >= 0 ? styles.positive : styles.negative}`}>
            {stats.avgPnlPct >= 0 ? '+' : ''}
            {stats.avgPnlPct}%
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Trades</div>
          <div className={styles.statValue}>{stats.totalTrades}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Today's Calls</div>
          <div className={styles.statValue}>{stats.todayCalls}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Best Trade</div>
          <div className={`${styles.statValue} ${styles.positive}`}>+{stats.bestPnlPct}%</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Worst Trade</div>
          <div className={`${styles.statValue} ${styles.negative}`}>{stats.worstPnlPct}%</div>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Long Win Rate</div>
          <div className={styles.statValue}>
            {stats.longWinRate}%<span className={styles.statSub}> ({stats.longCount} trades)</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Short Win Rate</div>
          <div className={styles.statValue}>
            {stats.shortWinRate}%<span className={styles.statSub}> ({stats.shortCount} trades)</span>
          </div>
        </div>
      </div>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Automation Tab                                                */
/* ══════════════════════════════════════════════════════════════ */

const AutomationTab: React.FC<{
  config: StrategistConfig | null;
  onConfigChange: (key: keyof StrategistConfig, value: any) => void;
}> = ({ config, onConfigChange }) => (
  <>
    <div className={styles.configPanel} data-tour="config-panel">
      <h3 className={styles.configTitle}>Automation Settings</h3>
      <p className={styles.configIntro}>
        Control how aggressively the strategist can act. Keep this tucked away from the review loop unless you are
        intentionally changing execution behavior.
      </p>
      <div className={styles.configGrid}>
        <div className={styles.configField}>
          <label className={styles.configLabel}>Autonomy Level</label>
          <select
            title="Autonomy Level"
            className={styles.configSelect}
            value={config?.autonomyLevel ?? 'full_auto'}
            onChange={(e) => onConfigChange('autonomyLevel', e.target.value)}
          >
            <option value="manual">Full Manual</option>
            <option value="semi_auto">Semi-Auto</option>
            <option value="full_auto">Full Auto</option>
          </select>
        </div>
        <div className={styles.configField}>
          <label className={styles.configLabel}>Personality</label>
          <select
            title="Trading Personality"
            className={styles.configSelect}
            value={config?.personality ?? 'balanced'}
            onChange={(e) => onConfigChange('personality', e.target.value)}
          >
            <option value="balanced">Balanced</option>
            <option value="conservative">Conservative Income</option>
            <option value="momentum">Momentum Scalper</option>
            <option value="contrarian">Contrarian Value</option>
          </select>
        </div>
        <div className={styles.configField}>
          <label className={styles.configLabel}>Max Positions</label>
          <select
            title="Max Positions"
            className={styles.configSelect}
            value={config?.maxConcurrentPositions ?? 5}
            onChange={(e) => onConfigChange('maxConcurrentPositions', Number(e.target.value))}
          >
            {[3, 5, 7, 10].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.configField}>
          <label className={styles.configLabel}>Max BP per Trade</label>
          <select
            title="Max BP per Trade"
            className={styles.configSelect}
            value={config?.maxBuyingPowerPct ?? 25}
            onChange={(e) => onConfigChange('maxBuyingPowerPct', Number(e.target.value))}
          >
            {[10, 15, 20, 25, 30, 40, 50].map((n) => (
              <option key={n} value={n}>
                {n}%
              </option>
            ))}
          </select>
        </div>
        <div className={styles.configField}>
          <label className={styles.configLabel}>Daily P&L Target %</label>
          <select
            title="Daily P&L Target"
            className={styles.configSelect}
            value={config?.dailyPnlTargetPct ?? 0}
            onChange={(e) => onConfigChange('dailyPnlTargetPct', Number(e.target.value))}
          >
            <option value={0}>Off</option>
            {[0.5, 1, 1.5, 2, 3, 5].map((n) => (
              <option key={n} value={n}>
                +{n}%
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>

    <div className={styles.personalityCard}>
      {(config?.personality ?? 'balanced') === 'balanced' && (
        <>
          <h3 className={styles.personalityTitle}>Balanced</h3>
          <p className={styles.personalityDesc}>
            Mix long and short opportunities across sectors. Take high-conviction trades in either direction. Hold
            winners, cut losers quickly. Target consistent risk-adjusted returns.
          </p>
          <PersonalityMeta risk="Medium" holdingPeriod="1-5 days" tradeFrequency="Moderate" />
          <div className={styles.personalityExample}>
            <span className={styles.personalityExampleLabel}>Example trade:</span>
            RSI(2) drops to 8 on NVDA while sector is neutral. Enter long with 3% stop, TP1 at +3%, trail the rest.
          </div>
        </>
      )}
      {config?.personality === 'conservative' && (
        <>
          <h3 className={styles.personalityTitle}>Conservative Income</h3>
          <p className={styles.personalityDesc}>
            Capital preservation above all. Only the highest-conviction setups with extreme RSI readings. Prefer
            large-cap, liquid names. Smaller positions, tight stops, take profits early.
          </p>
          <PersonalityMeta risk="Low" holdingPeriod="1-3 days" tradeFrequency="Low" />
          <div className={styles.personalityExample}>
            <span className={styles.personalityExampleLabel}>Example trade:</span>
            SPY RSI(2) hits 3 during a broad selloff. Small long position, -3% hard stop, close at TP1 (+3%). No
            earnings plays.
          </div>
        </>
      )}
      {config?.personality === 'momentum' && (
        <>
          <h3 className={styles.personalityTitle}>Momentum Scalper</h3>
          <p className={styles.personalityDesc}>
            Hunt strong directional moves with volume confirmation. Enter breakouts aggressively. Ride winners hard with
            trailing stops. Higher turnover, bigger position sizes.
          </p>
          <PersonalityMeta risk="High" holdingPeriod="Intraday - 2 days" tradeFrequency="High" />
          <div className={styles.personalityExample}>
            <span className={styles.personalityExampleLabel}>Example trade:</span>
            MSTR breaks above 20-day high with 3x relative volume. Enter long with trailing stop at -2%. Let it run.
          </div>
        </>
      )}
      {config?.personality === 'contrarian' && (
        <>
          <h3 className={styles.personalityTitle}>Contrarian Value</h3>
          <p className={styles.personalityDesc}>
            Buy when others are fearful, sell when others are greedy. Focus on deeply oversold names with strong
            fundamentals. Fade extreme sentiment. Patient, multi-day swing trades.
          </p>
          <PersonalityMeta risk="Medium-High" holdingPeriod="3-10 days" tradeFrequency="Low" />
          <div className={styles.personalityExample}>
            <span className={styles.personalityExampleLabel}>Example trade:</span>
            XLE drops 8% on panic selling while oil fundamentals remain strong. Enter long at RSI(2)=3, hold for mean
            reversion over 5-7 days.
          </div>
        </>
      )}
    </div>
  </>
);

const PersonalityMeta: React.FC<{ risk: string; holdingPeriod: string; tradeFrequency: string }> = ({
  risk,
  holdingPeriod,
  tradeFrequency,
}) => (
  <div className={styles.personalityMeta}>
    <div className={styles.personalityMetaItem}>
      <span className={styles.personalityMetaLabel}>Risk Level</span>
      <span className={styles.personalityMetaValue}>{risk}</span>
    </div>
    <div className={styles.personalityMetaItem}>
      <span className={styles.personalityMetaLabel}>Holding Period</span>
      <span className={styles.personalityMetaValue}>{holdingPeriod}</span>
    </div>
    <div className={styles.personalityMetaItem}>
      <span className={styles.personalityMetaLabel}>Trade Frequency</span>
      <span className={styles.personalityMetaValue}>{tradeFrequency}</span>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════ */
/* Actions Tab                                                   */
/* ══════════════════════════════════════════════════════════════ */

const ActionsTab: React.FC<{
  actions: StrategistAction[];
  onApprove: (sk: string) => void;
  onReject: (sk: string) => void;
  actionLoading: string | null;
}> = ({ actions, onApprove, onReject, actionLoading }) => {
  if (!actions.length) {
    return (
      <div className={styles.emptyState}>
        <p>No actions in queue</p>
        <p style={{ fontSize: '0.85rem' }}>Run the strategist or wait for the next scheduled run.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Source</th>
            <th>Symbol</th>
            <th>Action</th>
            <th>Qty</th>
            <th>Confidence</th>
            <th>Urgency</th>
            <th>Status</th>
            <th>Reason</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {actions.map((a) => (
            <tr key={a.SK}>
              <td>{timeAgo(a.createdAt)}</td>
              <td>
                <span
                  className={styles.badge}
                  style={{
                    background: a.source === 'bot' ? '#7c3aed' : '#2563eb',
                    color: '#fff',
                    fontSize: '0.7rem',
                    padding: '2px 6px',
                  }}
                  title={a.source === 'bot' ? `Bot: ${a.botName || a.botId || ''}` : 'AI Strategist'}
                >
                  {a.source === 'bot' ? `Bot` : 'Strat'}
                </span>
              </td>
              <td className={styles.symbol}>{a.symbol}</td>
              <td>{a.action.replace(/_/g, ' ')}</td>
              <td>{a.qty || (a.notional ? `$${a.notional.toFixed(0)}` : '—')}</td>
              <td>
                <div className={styles.confBar}>
                  <div className={styles.confTrack}>
                    <div
                      className={`${styles.confFill} ${confColor(a.confidence)}`}
                      style={{ width: `${a.confidence}%` }}
                    />
                  </div>
                  <span className={styles.confLabel}>{a.confidence}%</span>
                </div>
              </td>
              <td>
                <span className={urgencyClass(a.urgency)}>{a.urgency}</span>
              </td>
              <td>
                <span className={`${styles.badge} ${statusBadgeClass(a.status)}`}>{statusLabel(a.status)}</span>
              </td>
              <td className={styles.reason}>
                {a.reason}
                {a.status === 'failed' && a.failReason && (
                  <div style={{ color: 'var(--color-negative)', fontSize: '0.8rem', marginTop: 4 }}>{a.failReason}</div>
                )}
              </td>
              <td>
                {a.status === 'awaiting_approval' && (
                  <div className={styles.actionBtns}>
                    <button
                      className={styles.btnApprove}
                      onClick={() => onApprove(a.SK)}
                      disabled={actionLoading === a.SK}
                    >
                      Approve
                    </button>
                    <button
                      className={styles.btnReject}
                      onClick={() => onReject(a.SK)}
                      disabled={actionLoading === a.SK}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Journal Tab                                                   */
/* ══════════════════════════════════════════════════════════════ */

const JournalTab: React.FC<{ decisions: StrategistDecision[] }> = ({ decisions }) => {
  if (!decisions.length) {
    return (
      <div className={styles.emptyState}>
        <p>No decisions recorded yet</p>
        <p style={{ fontSize: '0.85rem' }}>The journal populates as the strategist runs.</p>
      </div>
    );
  }

  return (
    <>
      <TradeTimeline decisions={decisions} />
      <h3 className={styles.sectionTitle}>Trade Log</h3>
      {decisions.map((d) => (
        <div key={d.SK} className={styles.decisionCard}>
          <div className={styles.decisionHeader}>
            <div>
              <span className={styles.decisionSymbol}>{d.symbol}</span>
              <span className={styles.decisionAction}>
                {d.action.replace(/_/g, ' ')} x{d.qty}
              </span>
            </div>
            <span className={`${styles.badge} ${statusBadgeClass(d.status)}`}>{statusLabel(d.status)}</span>
          </div>
          <div className={styles.decisionReason}>{d.reasoning}</div>
          <div className={styles.decisionMeta}>
            <span>Entry: ${d.entryPrice?.toFixed(2) ?? '—'}</span>
            {d.exitPrice != null && <span>Exit: ${d.exitPrice.toFixed(2)}</span>}
            {d.pnl != null && (
              <span className={d.pnl >= 0 ? styles.positive : styles.negative}>
                P&L: ${d.pnl.toFixed(2)} ({d.pnlPct != null ? `${d.pnlPct.toFixed(1)}%` : '—'})
              </span>
            )}
            {d.stopLoss != null && <span>SL: ${d.stopLoss.toFixed(2)}</span>}
            {d.takeProfit != null && <span>TP: ${d.takeProfit.toFixed(2)}</span>}
            <span>Conf: {d.confidence}%</span>
            <span>{timeAgo(d.createdAt)}</span>
          </div>
        </div>
      ))}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Trade Replay Tab                                              */
/* ══════════════════════════════════════════════════════════════ */

const formatDateTime = (iso?: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMoney = (value?: number): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return `$${value.toFixed(2)}`;
};

const formatPct = (value?: number): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

type ReviewInboxItem = {
  key: string;
  source: 'Trade' | 'Skipped Trade' | 'Lesson' | 'Weekly';
  title: string;
  detail: string;
  meta: string;
  priority: number;
  tab: Tab;
  action: string;
  focusKey?: string;
  prompts: string[];
  tone: 'urgent' | 'review' | 'lesson' | 'weekly';
};

type InboxFilter = 'all' | ReviewInboxItem['source'];

const INBOX_FILTERS: Array<{ value: InboxFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'Trade', label: 'Trades' },
  { value: 'Skipped Trade', label: 'Skipped' },
  { value: 'Lesson', label: 'Lessons' },
  { value: 'Weekly', label: 'Weekly' },
];

const REVIEW_INBOX_STORAGE_KEY = 'signaldeck.reviewInbox.reviewed.v1';
const REVIEW_INBOX_NOTES_STORAGE_KEY = 'signaldeck.reviewInbox.notes.v1';

const readReviewedInboxKeys = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(REVIEW_INBOX_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []);
  } catch {
    return new Set();
  }
};

const readReviewInboxNotes = (): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(REVIEW_INBOX_NOTES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.entries(parsed).reduce<Record<string, string>>((notes, [key, value]) => {
      if (typeof value === 'string') {
        notes[key] = value;
      }
      return notes;
    }, {});
  } catch {
    return {};
  }
};

const sortNewest = <T extends { createdAt?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

const buildReviewInboxItems = ({
  decisions,
  gateReview,
  postmortems,
  reviews,
  runs,
}: {
  decisions: StrategistDecision[];
  gateReview: StrategistGateReview | null;
  postmortems: PostMortemAnalysis[];
  reviews: WeeklyReview[];
  runs: StrategistRun[];
}): ReviewInboxItem[] => {
  const items: ReviewInboxItem[] = [];
  const closedTrades = sortNewest(
    decisions.filter(
      (decision) => decision.action !== 'HOLD' && decision.status !== 'open' && decision.status !== 'expired',
    ),
  );
  const losingTrades = closedTrades.filter(
    (decision) => decision.status === 'closed_loss' || (decision.pnlPct != null && decision.pnlPct < 0),
  );
  const winningTrades = closedTrades.filter(
    (decision) => decision.status === 'closed_profit' || (decision.pnlPct != null && decision.pnlPct > 0),
  );
  const postmortemDecisionKeys = new Set(postmortems.map((postmortem) => postmortem.decisionSK).filter(Boolean));

  losingTrades.slice(0, 3).forEach((decision, index) => {
    const needsPostmortem = !postmortemDecisionKeys.has(decision.SK);
    items.push({
      key: `trade-loss-${decision.SK}`,
      source: 'Trade',
      title: `Replay ${decision.symbol} loss`,
      detail: needsPostmortem
        ? 'Closed red and has no post-mortem attached yet. Replay the thesis while it is fresh.'
        : decision.reasoning || 'Review the entry, exit, and original thesis.',
      meta: `${formatPct(decision.pnlPct)} · ${timeAgo(decision.closedAt || decision.createdAt)}`,
      priority: 100 - index * 4 + (needsPostmortem ? 6 : 0),
      tab: 'replay',
      action: 'Replay trade',
      focusKey: decision.SK,
      prompts: [
        'Was the entry too early, too late, or correctly timed?',
        'Which part of the original thesis failed first?',
        'Would I take this setup again with the same size?',
      ],
      tone: 'urgent',
    });
  });

  if (losingTrades.length < 2) {
    winningTrades.slice(0, 1).forEach((decision) => {
      items.push({
        key: `trade-win-${decision.SK}`,
        source: 'Trade',
        title: `Capture why ${decision.symbol} worked`,
        detail: decision.reasoning || 'Record what was repeatable about this winning decision.',
        meta: `${formatPct(decision.pnlPct)} · ${timeAgo(decision.closedAt || decision.createdAt)}`,
        priority: 72,
        tab: 'replay',
        action: 'Replay trade',
        focusKey: decision.SK,
        prompts: [
          'What made this trade repeatable?',
          'Was the exit disciplined or just lucky?',
          'What condition would invalidate this setup next time?',
        ],
        tone: 'review',
      });
    });
  }

  const gateCandidates = Array.isArray(gateReview?.candidates) ? gateReview.candidates : [];
  gateCandidates.slice(0, 3).forEach((candidate, index) => {
    const gateKey = `${candidate.gateLogSK || candidate.runId}:${candidate.symbol || 'UNKNOWN'}`;
    const gateItemKey = `gate-${candidate.symbol || 'UNKNOWN'}-${candidate.lastGate || 'gate'}-${
      candidate.reason || index
    }`;
    items.push({
      key: gateItemKey,
      source: 'Skipped Trade',
      title: `Review skipped ${candidate.symbol || 'candidate'}`,
      detail: candidate.reason || `Stopped at ${gateLabel(candidate.lastGate || 'gate')}.`,
      meta: `${gateLabel(candidate.lastGate || 'gate')} · ${timeAgo(candidate.createdAt)}`,
      priority: 88 - index * 5,
      tab: 'gates',
      action: 'Open skipped trade',
      focusKey: gateKey,
      prompts: [
        'Was this rejection protecting the account or filtering too aggressively?',
        'What extra scanner rule would catch this earlier?',
        'Would I override this gate in live trading?',
      ],
      tone: 'review',
    });
  });

  sortNewest(postmortems)
    .slice(0, 3)
    .forEach((postmortem, index) => {
      items.push({
        key: `lesson-${postmortem.SK}`,
        source: 'Lesson',
        title: `Apply ${postmortem.symbol} lesson`,
        detail: postmortem.lesson || postmortem.suggestedAdjustment || postmortem.whatHappened,
        meta: `${outcomeLabel(postmortem.outcomeCategory)} · ${timeAgo(postmortem.createdAt)}`,
        priority: 78 - index * 4,
        tab: 'postmortem',
        action: 'Read lesson',
        focusKey: postmortem.SK,
        prompts: [
          'What behavior should change before the next similar trade?',
          'Is this lesson specific enough to become a rule?',
          'How will I notice this mistake in real time?',
        ],
        tone: 'lesson',
      });
    });

  const latestReview = sortNewest(reviews)[0];
  if (latestReview) {
    items.push({
      key: `weekly-${latestReview.SK}`,
      source: 'Weekly',
      title: 'Read the latest weekly review',
      detail: latestReview.keyLesson || latestReview.summary,
      meta: `${latestReview.trackRecord.totalTrades} trades · ${latestReview.trackRecord.winRate}% win rate`,
      priority: 68,
      tab: 'weekly',
      action: 'Open weekly',
      prompts: [
        'What is the one adjustment to carry into next week?',
        'Which bias change matters most right now?',
        'What should stay unchanged because it is working?',
      ],
      tone: 'weekly',
    });
  } else if (runs.length > 0 || decisions.length > 0) {
    items.push({
      key: 'weekly-pending',
      source: 'Weekly',
      title: 'Weekly review is pending',
      detail: 'Recent strategist activity exists, but no weekly self-review has been generated yet.',
      meta: `${runs.length} recent run${runs.length === 1 ? '' : 's'}`,
      priority: 54,
      tab: 'weekly',
      action: 'Open weekly',
      prompts: [
        'What pattern showed up across the latest runs?',
        'Which trade deserves the first post-week review?',
        'What should be measured before changing the strategy?',
      ],
      tone: 'weekly',
    });
  }

  return items
    .filter((item) => item.detail)
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 8);
};

const DashboardInboxSummary: React.FC<{
  decisions: StrategistDecision[];
  gateReview: StrategistGateReview | null;
  gateLoading: boolean;
  postmortems: PostMortemAnalysis[];
  reviews: WeeklyReview[];
  runs: StrategistRun[];
  onNavigate: (tab: Tab, focusKey?: string) => void;
}> = ({ decisions, gateReview, gateLoading, postmortems, reviews, runs, onNavigate }) => {
  const reviewedKeys = React.useMemo(() => readReviewedInboxKeys(), []);
  const items = React.useMemo(
    () => buildReviewInboxItems({ decisions, gateReview, postmortems, reviews, runs }),
    [decisions, gateReview, postmortems, reviews, runs],
  );
  const openItems = React.useMemo(() => items.filter((item) => !reviewedKeys.has(item.key)), [items, reviewedKeys]);
  const counts = React.useMemo(
    () =>
      openItems.reduce<Record<ReviewInboxItem['source'], number>>(
        (acc, item) => {
          acc[item.source] += 1;
          return acc;
        },
        { Trade: 0, 'Skipped Trade': 0, Lesson: 0, Weekly: 0 },
      ),
    [openItems],
  );
  const topItem = openItems[0];
  const allReviewed = items.length > 0 && openItems.length === 0;

  return (
    <section className={styles.dashboardInbox} aria-labelledby="dashboard-inbox-title">
      <div className={styles.dashboardInboxMain}>
        <span className={styles.dashboardInboxEyebrow}>Review Inbox</span>
        <h3 id="dashboard-inbox-title">
          {allReviewed ? 'Inbox clear' : `${openItems.length} open review item${openItems.length === 1 ? '' : 's'}`}
        </h3>
        <p>
          {topItem
            ? `${topItem.source}: ${topItem.title}. ${topItem.meta}`
            : gateLoading
              ? 'Refreshing skipped trade candidates...'
              : allReviewed
                ? 'All current review items are marked done.'
                : 'No trades, skipped candidates, lessons, or weekly reviews need attention yet.'}
        </p>
      </div>
      <div className={styles.dashboardInboxBreakdown} aria-label="Open inbox breakdown">
        <div>
          <span>Trades</span>
          <strong>{counts.Trade}</strong>
        </div>
        <div>
          <span>Skipped</span>
          <strong>{counts['Skipped Trade']}</strong>
        </div>
        <div>
          <span>Lessons</span>
          <strong>{counts.Lesson}</strong>
        </div>
        <div>
          <span>Weekly</span>
          <strong>{counts.Weekly}</strong>
        </div>
      </div>
      <button type="button" className={styles.dashboardInboxButton} onClick={() => onNavigate('inbox')}>
        Open Inbox
      </button>
    </section>
  );
};

const ReviewInboxTab: React.FC<{
  decisions: StrategistDecision[];
  gateReview: StrategistGateReview | null;
  gateLoading: boolean;
  postmortems: PostMortemAnalysis[];
  reviews: WeeklyReview[];
  runs: StrategistRun[];
  onNavigate: (tab: Tab, focusKey?: string) => void;
  onPromoteRule: (draft: { text: string; source: StrategyRuleSource; sourceTitle: string }) => void;
}> = ({ decisions, gateReview, gateLoading, postmortems, reviews, runs, onNavigate, onPromoteRule }) => {
  const [filter, setFilter] = React.useState<InboxFilter>('all');
  const [showReviewed, setShowReviewed] = React.useState(false);
  const [reviewedKeys, setReviewedKeys] = React.useState<Set<string>>(() => readReviewedInboxKeys());
  const [reviewNotes, setReviewNotes] = React.useState<Record<string, string>>(() => readReviewInboxNotes());
  const items = React.useMemo(
    () => buildReviewInboxItems({ decisions, gateReview, postmortems, reviews, runs }),
    [decisions, gateReview, postmortems, reviews, runs],
  );
  const filteredItems = React.useMemo(
    () => (filter === 'all' ? items : items.filter((item) => item.source === filter)),
    [filter, items],
  );
  const visibleItems = React.useMemo(
    () => (showReviewed ? filteredItems : filteredItems.filter((item) => !reviewedKeys.has(item.key))),
    [filteredItems, reviewedKeys, showReviewed],
  );
  const filterCounts = React.useMemo(
    () =>
      items.reduce<Record<InboxFilter, number>>(
        (acc, item) => {
          acc.all += 1;
          acc[item.source] += 1;
          return acc;
        },
        { all: 0, Trade: 0, 'Skipped Trade': 0, Lesson: 0, Weekly: 0 },
      ),
    [items],
  );
  const counts = React.useMemo(
    () =>
      visibleItems.reduce<Record<ReviewInboxItem['source'], number>>(
        (acc, item) => {
          acc[item.source] += 1;
          return acc;
        },
        { Trade: 0, 'Skipped Trade': 0, Lesson: 0, Weekly: 0 },
      ),
    [visibleItems],
  );
  const reviewedCount = React.useMemo(
    () => filteredItems.reduce((count, item) => count + (reviewedKeys.has(item.key) ? 1 : 0), 0),
    [filteredItems, reviewedKeys],
  );
  const openCount = filteredItems.length - reviewedCount;

  React.useEffect(() => {
    try {
      window.localStorage.setItem(REVIEW_INBOX_STORAGE_KEY, JSON.stringify(Array.from(reviewedKeys)));
    } catch {
      // Local review state is a convenience only; ignore storage failures.
    }
  }, [reviewedKeys]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(REVIEW_INBOX_NOTES_STORAGE_KEY, JSON.stringify(reviewNotes));
    } catch {
      // Local notes are a convenience only; ignore storage failures.
    }
  }, [reviewNotes]);

  const markReviewed = (key: string) => {
    setReviewedKeys((current) => {
      const next = new Set(current);
      next.add(key);
      return next;
    });
  };

  const updateReviewNote = (key: string, value: string) => {
    setReviewNotes((current) => {
      const next = { ...current };
      if (value.trim()) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  if (!items.length) {
    return (
      <div className={styles.emptyState}>
        <p>Inbox is clear</p>
        <p style={{ fontSize: '0.85rem' }}>
          New trades, skipped candidates, lessons, and weekly reviews will collect here.
        </p>
      </div>
    );
  }

  return (
    <>
      <section className={styles.inboxHeader} aria-labelledby="review-inbox-title">
        <div>
          <span>Daily Review</span>
          <h2 id="review-inbox-title">Review Inbox</h2>
          <p>Work the highest-signal items first, then jump into the source tab for deeper context.</p>
        </div>
        <div className={styles.inboxCount}>
          <strong>{visibleItems.length}</strong>
          <span>{showReviewed ? 'shown' : 'open'}</span>
        </div>
      </section>

      <div className={styles.inboxControls}>
        <span>
          {reviewedCount} reviewed · {openCount} open
        </span>
        <button type="button" className={styles.inboxToggle} onClick={() => setShowReviewed((current) => !current)}>
          {showReviewed ? 'Hide reviewed' : 'Show reviewed'}
        </button>
      </div>

      <div className={styles.inboxFilters} aria-label="Inbox filters">
        {INBOX_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`${styles.inboxFilter} ${filter === option.value ? styles.inboxFilterActive : ''}`}
            onClick={() => setFilter(option.value)}
          >
            <span>{option.label}</span>
            <strong>{filterCounts[option.value]}</strong>
          </button>
        ))}
      </div>

      <div className={styles.inboxStats} aria-label="Inbox breakdown">
        <div>
          <span>Trades</span>
          <strong>{counts.Trade}</strong>
        </div>
        <div>
          <span>Skipped</span>
          <strong>{counts['Skipped Trade']}</strong>
        </div>
        <div>
          <span>Lessons</span>
          <strong>{counts.Lesson}</strong>
        </div>
        <div>
          <span>Weekly</span>
          <strong>{counts.Weekly}</strong>
        </div>
      </div>

      {gateLoading && (
        <div className={styles.inboxNotice}>Skipped trade candidates are still refreshing and may update shortly.</div>
      )}

      {!visibleItems.length && (
        <div className={styles.emptyState}>
          <p>{filteredItems.length ? 'Inbox reviewed' : 'No items in this filter'}</p>
          <p style={{ fontSize: '0.85rem' }}>
            {filteredItems.length
              ? 'All current review items in this filter are marked done.'
              : 'Choose another filter or wait for new review items.'}
          </p>
        </div>
      )}

      <div className={styles.inboxList}>
        {visibleItems.map((item) => {
          const reviewed = reviewedKeys.has(item.key);
          return (
            <article key={item.key} className={`${styles.inboxItem} ${reviewed ? styles.inboxItemReviewed : ''}`}>
              <div className={styles.inboxItemMain}>
                <span className={`${styles.inboxSource} ${styles[`inboxSource_${item.tone}`]}`}>{item.source}</span>
                <h3>{item.title}</h3>
                <p>{item.detail}</p>
                {item.prompts.length > 0 && (
                  <div className={styles.inboxPrompts}>
                    <span>Review prompts</span>
                    <ul>
                      {item.prompts.map((prompt) => (
                        <li key={prompt}>{prompt}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <label className={styles.inboxNote}>
                  <span>Review note</span>
                  <textarea
                    value={reviewNotes[item.key] ?? ''}
                    onChange={(event) => updateReviewNote(item.key, event.target.value)}
                    placeholder="Write the decision takeaway here..."
                    rows={3}
                  />
                </label>
                <span className={styles.inboxMeta}>{item.meta}</span>
              </div>
              <div className={styles.inboxActions}>
                {reviewed && <span className={styles.inboxReviewedBadge}>Reviewed</span>}
                <button
                  type="button"
                  className={styles.inboxAction}
                  onClick={() => onNavigate(item.tab, item.focusKey)}
                >
                  {item.action}
                </button>
                {!reviewed && (
                  <button type="button" className={styles.inboxActionSecondary} onClick={() => markReviewed(item.key)}>
                    Mark reviewed
                  </button>
                )}
                <button
                  type="button"
                  className={styles.inboxActionSecondary}
                  onClick={() =>
                    onPromoteRule({
                      text: reviewNotes[item.key] || item.detail,
                      source: 'Inbox',
                      sourceTitle: item.title,
                    })
                  }
                >
                  Promote to rule
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
};

const getHoldTime = (decision: StrategistDecision): string => {
  if (!decision.createdAt || !decision.closedAt) return decision.status === 'open' ? 'Open' : '—';
  const started = new Date(decision.createdAt).getTime();
  const ended = new Date(decision.closedAt).getTime();
  if (Number.isNaN(started) || Number.isNaN(ended) || ended < started) return '—';
  const hours = Math.max(1, Math.round((ended - started) / 3600000));
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
};

const matchPostmortem = (
  decision: StrategistDecision,
  postmortems: PostMortemAnalysis[],
): PostMortemAnalysis | undefined => {
  const direct = postmortems.find((pm) => pm.decisionSK === decision.SK);
  if (direct) return direct;
  return postmortems.find(
    (pm) =>
      pm.symbol === decision.symbol &&
      pm.action === decision.action &&
      (!decision.closedAt ||
        Math.abs(new Date(pm.createdAt).getTime() - new Date(decision.closedAt).getTime()) < 86400000),
  );
};

const TradeReplayTab: React.FC<{
  decisions: StrategistDecision[];
  postmortems: PostMortemAnalysis[];
  focusedSK?: string | null;
  onPromoteRule: (draft: { text: string; source: StrategyRuleSource; sourceTitle: string }) => void;
}> = ({ decisions, postmortems, focusedSK, onPromoteRule }) => {
  const replayable = React.useMemo(
    () =>
      decisions
        .filter((d) => d.action !== 'HOLD')
        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()),
    [decisions],
  );
  const [selectedSK, setSelectedSK] = React.useState<string | null>(null);
  const [replayDetails, setReplayDetails] = React.useState<Record<string, StrategistReplay>>({});
  const [replayLoading, setReplayLoading] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!replayable.length) {
      setSelectedSK(null);
      return;
    }
    if (focusedSK && replayable.some((d) => d.SK === focusedSK) && selectedSK !== focusedSK) {
      setSelectedSK(focusedSK);
      return;
    }
    if (!selectedSK || !replayable.some((d) => d.SK === selectedSK)) {
      setSelectedSK(replayable[0].SK);
    }
  }, [focusedSK, replayable, selectedSK]);

  React.useEffect(() => {
    if (!selectedSK || replayDetails[selectedSK] || replayLoading === selectedSK) return;
    let cancelled = false;
    setReplayLoading(selectedSK);
    alpacaService
      .getStrategistReplay(selectedSK)
      .then((replay) => {
        if (cancelled) return;
        setReplayDetails((current) => ({ ...current, [selectedSK]: replay }));
      })
      .catch((err) => {
        console.error('Failed to load trade replay:', err);
      })
      .finally(() => {
        if (!cancelled) setReplayLoading(null);
      });
    return () => {
      cancelled = true;
    };
  }, [replayDetails, replayLoading, selectedSK]);

  if (!replayable.length) {
    return (
      <div className={styles.emptyState}>
        <p>No trades to replay yet</p>
        <p style={{ fontSize: '0.85rem' }}>Executed strategist decisions will appear here with their audit trail.</p>
      </div>
    );
  }

  const selected = replayable.find((d) => d.SK === selectedSK) ?? replayable[0];
  const replay = replayDetails[selected.SK];
  const postmortem = replay?.postmortem ?? matchPostmortem(selected, postmortems);
  const isClosed = selected.status === 'closed_profit' || selected.status === 'closed_loss';
  const outcomeClass = (selected.pnlPct ?? 0) >= 0 ? styles.positive : styles.negative;
  const thesisStatus = postmortem
    ? outcomeLabel(postmortem.outcomeCategory)
    : isClosed
      ? 'Awaiting Post-Mortem'
      : 'In Progress';
  const fallbackTimeline = [
    {
      key: 'scanner',
      label: 'Scanner Signal',
      status: 'local',
      at: selected.createdAt,
      summary: `${selected.strategy || 'UNKNOWN'} setup, ${
        selected.direction || (selected.action.includes('SHORT') ? 'SHORT' : 'LONG')
      } bias.`,
      details: selected.reasoning,
    },
    {
      key: 'analyst',
      label: 'Analyst Decision',
      status: 'local',
      at: selected.createdAt,
      summary: `${selected.confidence}% confidence for ${selected.qty} share${selected.qty === 1 ? '' : 's'}.`,
      details: `${selected.urgency || 'today'} urgency.`,
    },
    {
      key: 'risk_plan',
      label: 'Risk Plan',
      status: 'local',
      at: selected.createdAt,
      summary: `Stop ${formatMoney(selected.stopLoss)}, target ${formatMoney(selected.takeProfit)}.`,
      details: `Regime ${selected.regime || selected.regimeOutlook || 'not recorded'}.`,
    },
    {
      key: 'outcome',
      label: 'Outcome',
      status: selected.status,
      at: selected.closedAt,
      summary: isClosed
        ? `Closed at ${formatMoney(selected.exitPrice)} for ${formatPct(selected.pnlPct)}.`
        : 'Position is still open.',
      details: '',
    },
  ];
  const timeline = replay?.timeline?.length ? replay.timeline : fallbackTimeline;
  const gateEvents = replay?.gateEvents ?? [];
  const activeRules = replay?.activeRules ?? [];
  const ruleScopeLabel = replay?.ruleScope === 'decision_snapshot' ? 'Snapshot at decision' : 'Current active library';

  return (
    <div className={styles.replayShell}>
      <div className={styles.replayList} aria-label="Trades">
        {replayable.map((trade) => {
          const active = trade.SK === selected.SK;
          const pnlClass = (trade.pnlPct ?? 0) >= 0 ? styles.positive : styles.negative;
          return (
            <button
              key={trade.SK}
              type="button"
              className={`${styles.replayListItem} ${active ? styles.replayListItemActive : ''}`}
              onClick={() => setSelectedSK(trade.SK)}
            >
              <span className={styles.replayListTop}>
                <strong>{trade.symbol}</strong>
                <span className={`${styles.badge} ${statusBadgeClass(trade.status)}`}>{statusLabel(trade.status)}</span>
              </span>
              <span className={styles.replayListAction}>{trade.action.replace(/_/g, ' ')}</span>
              <span className={styles.replayListMeta}>
                <span>{formatDateTime(trade.createdAt)}</span>
                {trade.pnlPct != null && <span className={pnlClass}>{formatPct(trade.pnlPct)}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.replayDetail}>
        <div className={styles.replayHero}>
          <div>
            <span className={styles.replayEyebrow}>Decision Audit</span>
            <h3 className={styles.replayTitle}>
              {selected.symbol} · {selected.action.replace(/_/g, ' ')}
            </h3>
            <p className={styles.replaySubtitle}>{selected.reasoning || 'No reasoning stored for this decision.'}</p>
          </div>
          <div className={styles.replayOutcome}>
            <span className={`${styles.badge} ${statusBadgeClass(selected.status)}`}>
              {statusLabel(selected.status)}
            </span>
            {selected.pnlPct != null && <strong className={outcomeClass}>{formatPct(selected.pnlPct)}</strong>}
          </div>
        </div>

        <div className={styles.replayMetricGrid}>
          <div className={styles.replayMetric}>
            <span>Entry</span>
            <strong>{formatMoney(selected.entryPrice)}</strong>
          </div>
          <div className={styles.replayMetric}>
            <span>Exit</span>
            <strong>{formatMoney(selected.exitPrice)}</strong>
          </div>
          <div className={styles.replayMetric}>
            <span>P&L</span>
            <strong className={outcomeClass}>{selected.pnl != null ? formatMoney(selected.pnl) : '—'}</strong>
          </div>
          <div className={styles.replayMetric}>
            <span>Hold</span>
            <strong>{getHoldTime(selected)}</strong>
          </div>
        </div>

        <div className={styles.auditTimeline}>
          {replayLoading === selected.SK && <div className={styles.replayLoading}>Loading full audit trail...</div>}
          {timeline.map((item) => (
            <div key={item.key} className={styles.auditStep}>
              <span className={styles.auditDot} />
              <div>
                <span className={styles.auditLabel}>
                  {item.label}
                  <em>{item.status}</em>
                </span>
                <p>
                  {item.at ? `${formatDateTime(item.at)} - ` : ''}
                  {item.summary}
                </p>
                {item.details && <p className={styles.auditDetails}>{item.details}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.replayInsightGrid}>
          <div className={styles.replayInsight}>
            <span className={styles.postmortemLabel}>Thesis Review</span>
            <strong>{thesisStatus}</strong>
            <p>{postmortem?.whatHappened || 'No post-mortem has been generated for this trade yet.'}</p>
          </div>
          <div className={styles.replayInsight}>
            <span className={styles.postmortemLabel}>Lesson</span>
            <strong>{postmortem?.confidenceCalibrated ? 'Confidence calibrated' : 'Calibration pending'}</strong>
            <p>{postmortem?.lesson || 'The strategist will attach a lesson after the trade is closed and reviewed.'}</p>
          </div>
          <div className={styles.replayInsight}>
            <span className={styles.postmortemLabel}>Rule Guardrails</span>
            <strong>
              {activeRules.length
                ? `${activeRules.length} active rule${activeRules.length === 1 ? '' : 's'}`
                : 'No active rules'}
            </strong>
            {activeRules.length ? (
              <ul className={styles.replayRuleList} aria-label={ruleScopeLabel}>
                {activeRules.slice(0, 3).map((rule) => (
                  <li key={rule.id || rule.text}>{rule.text}</li>
                ))}
              </ul>
            ) : (
              <p>Promoted rules marked active will appear here during future replay audits.</p>
            )}
            {activeRules.length > 0 && <span className={styles.replayRuleScope}>{ruleScopeLabel}</span>}
          </div>
          <div className={styles.replayInsight}>
            <span className={styles.postmortemLabel}>Gate Log</span>
            <strong>
              {gateEvents.length
                ? `${gateEvents.length} event${gateEvents.length === 1 ? '' : 's'} recorded`
                : 'No gate events'}
            </strong>
            <p>
              {gateEvents.length
                ? gateEvents
                    .slice(0, 3)
                    .map(
                      (event) =>
                        `${event.gate_name || event.gate || 'gate'}: ${event.status || event.result || 'recorded'}`,
                    )
                    .join(' · ')
                : 'Detailed gate events will appear when the selected trade can be matched to a strategist run.'}
            </p>
          </div>
        </div>

        {postmortem?.suggestedAdjustment && (
          <div className={styles.lessonBox}>
            <span className={styles.postmortemLabel}>Suggested adjustment</span>
            <span>{postmortem.suggestedAdjustment}</span>
          </div>
        )}
        <div className={styles.rulePromotionPanel}>
          <span className={styles.postmortemLabel}>Rule candidate</span>
          <p>
            {postmortem?.suggestedAdjustment ||
              postmortem?.lesson ||
              selected.reasoning ||
              'Promote this replay into a future guardrail.'}
          </p>
          <button
            type="button"
            className={styles.rulePromoteButton}
            onClick={() =>
              onPromoteRule({
                text: postmortem?.suggestedAdjustment || postmortem?.lesson || selected.reasoning,
                source: 'Replay',
                sourceTitle: `${selected.symbol} ${selected.action.replace(/_/g, ' ')}`,
              })
            }
          >
            Promote to rule
          </button>
        </div>
      </div>
    </div>
  );
};

const gateLabel = (gate: string) => gate.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const gateResultClass = (result: string) => {
  if (result === 'PASSED') return styles.gateResultPassed;
  if (result === 'REJECTED') return styles.gateResultRejected;
  return styles.gateResultNeutral;
};

const gateTheme = (gate: string, reason = ''): 'risk' | 'timing' | 'quality' | 'repeat' | 'general' => {
  const text = `${gate} ${reason}`.toLowerCase();
  if (text.includes('risk') || text.includes('size') || text.includes('loss') || text.includes('exposure'))
    return 'risk';
  if (text.includes('timing') || text.includes('early') || text.includes('late') || text.includes('confirm'))
    return 'timing';
  if (text.includes('volume') || text.includes('liquid') || text.includes('spread') || text.includes('quality'))
    return 'quality';
  return 'general';
};

const calibrationAction = (theme: ReturnType<typeof gateTheme>): string => {
  switch (theme) {
    case 'risk':
      return 'Tighten scanner thresholds or reduce default sizing before candidates reach the strategist.';
    case 'timing':
      return 'Add one more confirmation filter so scanner hits wait for better entry timing.';
    case 'quality':
      return 'Raise liquidity, volume, or spread quality requirements for scanner candidates.';
    case 'repeat':
      return 'Temporarily downgrade repeated names until their rejection reason clears.';
    default:
      return 'Review scanner breadth and keep only signals that survive downstream gates.';
  }
};

const asArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);

const followUpOutcome = (candidate: GateCandidate): string => String(candidate.followUp?.outcome || '');

const followUpReturn = (candidate: GateCandidate): number | null => {
  const value = candidate.followUp?.followUpReturn1hPct ?? candidate.followUp?.pnlPct;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const isMissedWinner = (candidate: GateCandidate): boolean =>
  ['later_winner', 'missed_winner'].includes(followUpOutcome(candidate));

const isCorrectReject = (candidate: GateCandidate): boolean =>
  ['later_loser', 'correct_reject'].includes(followUpOutcome(candidate));

const gateLearningAction = (missedRate: number, correctRate: number, gate: string): string => {
  if (missedRate >= 40) return `Loosen or resize ${gateLabel(gate)} before it blocks the next similar setup.`;
  if (correctRate >= 60) return `${gateLabel(gate)} is earning its keep; preserve this guardrail.`;
  return `Keep collecting outcomes before changing ${gateLabel(gate)}.`;
};

export const GateReviewTab: React.FC<{
  data: StrategistGateReview | null;
  loading: boolean;
  error: string | null;
  refreshedAt: string | null;
  onRefresh: () => void;
  onUpdateFollowups: () => void;
  followupLoading: boolean;
  focusedKey?: string | null;
}> = ({ data, loading, error, refreshedAt, onRefresh, onUpdateFollowups, followupLoading, focusedKey }) => {
  const [gateFilter, setGateFilter] = React.useState('all');
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const gateCandidates = React.useMemo(
    () =>
      asArray(data?.candidates).map((candidate, index) => ({
        ...candidate,
        symbol: candidate.symbol || 'UNKNOWN',
        gateLogSK: candidate.gateLogSK || `${candidate.runId || 'run'}:${candidate.symbol || 'unknown'}:${index}`,
        runId: candidate.runId || '',
        createdAt: candidate.createdAt || '',
        lastGate: candidate.lastGate || 'unknown',
        reason: candidate.reason || '',
        rejectedAt: candidate.rejectedAt || candidate.createdAt || '',
        rejectedPrice: candidate.rejectedPrice,
        direction: candidate.direction || '',
        strategy: candidate.strategy || '',
        followUpDueAt: candidate.followUpDueAt || '',
        followUpStatus: candidate.followUpStatus || 'pending_outcome',
        passedGates: asArray(candidate.passedGates),
        events: asArray(candidate.events),
        rejections: asArray(candidate.rejections),
        followUp: candidate.followUp ?? {
          outcome: 'untracked',
          label: 'No follow-up',
          detail: 'No follow-up data is attached to this rejection yet.',
        },
      })),
    [data?.candidates],
  );
  const gateOptions = React.useMemo(() => asArray(data?.gates), [data?.gates]);
  const gateRuns = React.useMemo(() => asArray(data?.runs), [data?.runs]);
  const candidates = React.useMemo(() => {
    const rows = gateCandidates;
    return gateFilter === 'all' ? rows : rows.filter((candidate) => candidate.lastGate === gateFilter);
  }, [gateCandidates, gateFilter]);
  const gateStats = React.useMemo(() => {
    const rows = gateCandidates;
    const counts = rows.reduce<Record<string, number>>((acc, candidate) => {
      const gate = candidate.lastGate || 'unknown';
      acc[gate] = (acc[gate] ?? 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([gate, count]) => ({
        gate,
        count,
        pct: rows.length ? Math.round((count / rows.length) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [gateCandidates]);
  const gateLearning = React.useMemo(() => {
    const byGate = gateCandidates.reduce<Record<string, GateCandidate[]>>((acc, candidate) => {
      const gate = candidate.lastGate || 'unknown';
      acc[gate] = acc[gate] ?? [];
      acc[gate].push(candidate);
      return acc;
    }, {});

    return Object.entries(byGate)
      .map(([gate, rows]) => {
        const evaluated = rows.filter((candidate) => isMissedWinner(candidate) || isCorrectReject(candidate));
        const missed = evaluated.filter(isMissedWinner);
        const correct = evaluated.filter(isCorrectReject);
        const missedRate = evaluated.length ? Math.round((missed.length / evaluated.length) * 100) : 0;
        const correctRate = evaluated.length ? Math.round((correct.length / evaluated.length) * 100) : 0;
        const avgReturn =
          evaluated.length > 0
            ? evaluated.reduce((sum, candidate) => sum + (followUpReturn(candidate) ?? 0), 0) / evaluated.length
            : null;
        return {
          gate,
          total: rows.length,
          evaluated: evaluated.length,
          missed: missed.length,
          correct: correct.length,
          missedRate,
          correctRate,
          avgReturn,
          action: gateLearningAction(missedRate, correctRate, gate),
        };
      })
      .sort((a, b) => {
        if (b.missedRate !== a.missedRate) return b.missedRate - a.missedRate;
        if (b.evaluated !== a.evaluated) return b.evaluated - a.evaluated;
        return b.total - a.total;
      })
      .slice(0, 4);
  }, [gateCandidates]);
  const repeatSymbols = React.useMemo(() => {
    const rows = gateCandidates;
    const counts = rows.reduce<Record<string, number>>((acc, candidate) => {
      acc[candidate.symbol] = (acc[candidate.symbol] ?? 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .filter(([, count]) => count > 1)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);
  }, [gateCandidates]);
  const followUpStats = React.useMemo(() => {
    const rows = gateCandidates.map((candidate) => candidate.followUp).filter(Boolean);
    const laterWinners = gateCandidates.filter(isMissedWinner).map((candidate) => candidate.followUp);
    const laterLosers = gateCandidates.filter(isCorrectReject).map((candidate) => candidate.followUp);
    const pending = rows.filter((followUp) => followUp?.outcome === 'pending');
    return {
      tracked: rows.filter((followUp) => followUp?.outcome && followUp.outcome !== 'untracked').length,
      laterWinners: laterWinners.length,
      laterLosers: laterLosers.length,
      pending: pending.length,
      untracked: rows.filter((followUp) => !followUp?.outcome || followUp.outcome === 'untracked').length,
      worstMissedWinner: laterWinners.slice().sort((a, b) => (b?.pnlPct ?? 0) - (a?.pnlPct ?? 0))[0],
    };
  }, [gateCandidates]);
  const latestRun = gateRuns[0];
  const diagnostics = data?.diagnostics;
  const totalGateEvents = diagnostics?.eventsScanned ?? gateRuns.reduce((sum, run) => sum + (run.totalEvents || 0), 0);
  const totalGateRejections =
    diagnostics?.rejectionsScanned ?? gateRuns.reduce((sum, run) => sum + (run.totalRejections || 0), 0);
  const logsScanned = diagnostics?.logsScanned ?? gateRuns.length;
  const latestGateLogAt = diagnostics?.latestGateLogAt ?? latestRun?.createdAt ?? null;
  const gateRejectRate = totalGateEvents ? Math.round((totalGateRejections / totalGateEvents) * 100) : 0;
  const latestRunAgeMs = latestGateLogAt ? Date.now() - new Date(latestGateLogAt).getTime() : null;
  const latestRunHealthy = latestRunAgeMs != null && latestRunAgeMs >= 0 && latestRunAgeMs < 45 * 60_000;
  const runHealthLabel = !latestRun
    ? 'No gate logs'
    : latestRunHealthy
      ? 'Recent'
      : latestRunAgeMs != null && latestRunAgeMs >= 0
        ? 'Stale'
        : 'Unknown';
  const topGate = gateStats[0];
  const calibrationSuggestions = React.useMemo(() => {
    const suggestions: Array<{
      key: string;
      label: string;
      title: string;
      detail: string;
      action: string;
      severity: 'high' | 'medium' | 'low';
    }> = [];
    if (topGate) {
      const theme = gateTheme(topGate.gate);
      suggestions.push({
        key: `gate-${topGate.gate}`,
        label: `${topGate.pct}% bottleneck`,
        title: `${gateLabel(topGate.gate)} is filtering the most ideas`,
        detail: `${topGate.count} of ${data?.count ?? gateCandidates.length} recent rejects stopped here.`,
        action: calibrationAction(theme),
        severity: topGate.pct >= 50 ? 'high' : topGate.pct >= 30 ? 'medium' : 'low',
      });
    }
    if (latestRun && latestRun.totalEvents > 0) {
      const rejectionRate = Math.round((latestRun.totalRejections / latestRun.totalEvents) * 100);
      if (rejectionRate >= 60) {
        suggestions.push({
          key: 'latest-run-noise',
          label: `${rejectionRate}% reject rate`,
          title: 'Scanner may be sending too many low-conviction setups',
          detail: `The latest run rejected ${latestRun.totalRejections} of ${latestRun.totalEvents} gate events.`,
          action: 'Narrow the scanner universe or raise confidence thresholds for the next run.',
          severity: rejectionRate >= 80 ? 'high' : 'medium',
        });
      }
    }
    if (repeatSymbols.length) {
      const repeated = repeatSymbols.map(([symbol, count]) => `${symbol} x${count}`).join(', ');
      suggestions.push({
        key: 'repeat-symbols',
        label: 'Repeated rejects',
        title: 'Some symbols keep failing gates',
        detail: repeated,
        action: calibrationAction('repeat'),
        severity: repeatSymbols.some(([, count]) => count >= 3) ? 'high' : 'medium',
      });
    }
    if (followUpStats.laterWinners > 0) {
      suggestions.push({
        key: 'followup-later-winners',
        label: `${followUpStats.laterWinners} later winner${followUpStats.laterWinners === 1 ? '' : 's'}`,
        title: 'Some rejected symbols later worked',
        detail: followUpStats.worstMissedWinner?.pnlPct
          ? `Largest later winner in this sample was ${formatPct(followUpStats.worstMissedWinner.pnlPct)}.`
          : 'Review these rejects before tightening the scanner further.',
        action: 'Inspect whether the gate delayed a good setup or blocked a trade that should have been resized.',
        severity: followUpStats.laterWinners >= 3 ? 'high' : 'medium',
      });
    }
    const timingGate = gateStats.find(({ gate }) => gateTheme(gate) === 'timing');
    if (timingGate && timingGate.gate !== topGate?.gate) {
      suggestions.push({
        key: `timing-${timingGate.gate}`,
        label: `${timingGate.count} timing rejects`,
        title: 'Timing gates are catching early ideas',
        detail: `${gateLabel(timingGate.gate)} rejected ${timingGate.count} recent candidate${
          timingGate.count === 1 ? '' : 's'
        }.`,
        action: calibrationAction('timing'),
        severity: timingGate.pct >= 30 ? 'medium' : 'low',
      });
    }
    return suggestions.slice(0, 4);
  }, [data?.count, followUpStats, gateCandidates.length, gateStats, latestRun, repeatSymbols, topGate]);

  React.useEffect(() => {
    if (!candidates.length) {
      setSelectedKey(null);
      return;
    }
    if (focusedKey && candidates.some((candidate) => `${candidate.gateLogSK}:${candidate.symbol}` === focusedKey)) {
      if (selectedKey !== focusedKey) setSelectedKey(focusedKey);
      return;
    }
    if (!selectedKey || !candidates.some((candidate) => `${candidate.gateLogSK}:${candidate.symbol}` === selectedKey)) {
      setSelectedKey(`${candidates[0].gateLogSK}:${candidates[0].symbol}`);
    }
  }, [candidates, focusedKey, selectedKey]);

  if (loading) {
    return (
      <div className={styles.emptyState}>
        <p>Loading gate review...</p>
        <p style={{ fontSize: '0.85rem' }}>Collecting recent rejected candidates and their gate history.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.emptyState}>
        <p>Gate review unavailable</p>
        <p style={{ fontSize: '0.85rem' }}>{error}</p>
        <button type="button" className={styles.btnSecondary} onClick={onRefresh}>
          Refresh Skipped Trades
        </button>
      </div>
    );
  }

  if (!data || !gateCandidates.length) {
    const emptyDetail = logsScanned
      ? `Scanned ${logsScanned} recent gate log${logsScanned === 1 ? '' : 's'} with ${totalGateEvents} gate event${
          totalGateEvents === 1 ? '' : 's'
        } and ${totalGateRejections} recorded rejection${totalGateRejections === 1 ? '' : 's'}.`
      : 'No strategist gate logs were returned for this account yet.';
    return (
      <div className={styles.emptyState}>
        <p>No rejected candidates yet</p>
        <p style={{ fontSize: '0.85rem' }}>{emptyDetail}</p>
        <div className={styles.gateEmptyMetrics}>
          <div>
            <span>Gate logs scanned</span>
            <strong>{logsScanned}</strong>
          </div>
          <div>
            <span>Gate events scanned</span>
            <strong>{totalGateEvents}</strong>
          </div>
          <div>
            <span>Latest run</span>
            <strong>{latestGateLogAt ? formatDateTime(latestGateLogAt) : 'None yet'}</strong>
          </div>
        </div>
        <p style={{ fontSize: '0.85rem' }}>
          Run Strategist manually or check again after the next scheduled scan to populate rejected scanner ideas.
        </p>
        <button type="button" className={styles.btnSecondary} onClick={onRefresh}>
          Refresh Skipped Trades
        </button>
      </div>
    );
  }

  const selected =
    candidates.find((candidate) => `${candidate.gateLogSK}:${candidate.symbol}` === selectedKey) ?? candidates[0];

  if (!selected) {
    return (
      <div className={styles.emptyState}>
        <p>No candidates for this gate</p>
        <p style={{ fontSize: '0.85rem' }}>Choose a different gate to inspect recent rejections.</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.gateRefreshBar}>
        <div>
          <span>Skipped Trades</span>
          <strong>
            {candidates.length} of {gateCandidates.length} reject{gateCandidates.length === 1 ? '' : 's'} shown
          </strong>
        </div>
        <div className={styles.gateRefreshActions}>
          <span>{refreshedAt ? `Last refreshed ${timeAgo(refreshedAt)}` : 'Not refreshed yet'}</span>
          <button type="button" className={styles.btnSecondary} onClick={onUpdateFollowups} disabled={followupLoading}>
            {followupLoading ? 'Updating...' : 'Update Outcomes'}
          </button>
          <button type="button" className={styles.btnSecondary} onClick={onRefresh} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className={styles.runHealthPanel}>
        <div>
          <span>Run Health</span>
          <strong>{runHealthLabel}</strong>
          <p>
            {latestGateLogAt ? `Latest gate log ${timeAgo(latestGateLogAt)}.` : 'No gate log has been recorded yet.'}
          </p>
        </div>
        <div>
          <span>Logs Scanned</span>
          <strong>{logsScanned}</strong>
          <p>
            {totalGateEvents} gate event{totalGateEvents === 1 ? '' : 's'} in this sample.
          </p>
        </div>
        <div>
          <span>Reject Rate</span>
          <strong>{gateRejectRate}%</strong>
          <p>
            {totalGateRejections} recorded rejection{totalGateRejections === 1 ? '' : 's'}.
          </p>
        </div>
        <div>
          <span>Candidates</span>
          <strong>{gateCandidates.length}</strong>
          <p>{candidates.length} visible with the current filter.</p>
        </div>
        <div>
          <span>Follow-Up</span>
          <strong>{followUpStats.tracked}</strong>
          <p>
            {followUpStats.laterWinners} later winner{followUpStats.laterWinners === 1 ? '' : 's'} ·{' '}
            {followUpStats.laterLosers} later loser{followUpStats.laterLosers === 1 ? '' : 's'}.
          </p>
        </div>
      </div>

      <div className={styles.gateSummaryGrid}>
        <div className={styles.gateSummaryCard}>
          <span>Top Bottleneck</span>
          <strong>{topGate ? gateLabel(topGate.gate) : 'No rejections'}</strong>
          <p>{topGate ? `${topGate.count} rejects, ${topGate.pct}% of recent candidates.` : 'No gate pressure yet.'}</p>
        </div>
        <div className={styles.gateSummaryCard}>
          <span>Latest Run</span>
          <strong>{latestRun ? `${latestRun.totalRejections}/${latestRun.totalEvents}` : 'No run data'}</strong>
          <p>
            {latestRun
              ? `${timeAgo(latestRun.createdAt)} with ${latestRun.totalRejections} rejects.`
              : 'Waiting for a strategist run.'}
          </p>
        </div>
        <div className={styles.gateSummaryCard}>
          <span>Repeat Symbols</span>
          <strong>{repeatSymbols.length ? repeatSymbols.map(([symbol]) => symbol).join(', ') : 'None'}</strong>
          <p>
            {repeatSymbols.length
              ? repeatSymbols.map(([symbol, count]) => `${symbol} x${count}`).join(' · ')
              : 'No symbol is repeatedly blocked in this sample.'}
          </p>
        </div>
        <div className={styles.gateSummaryCard}>
          <span>Rejected Follow-Up</span>
          <strong>
            {followUpStats.laterWinners
              ? `${followUpStats.laterWinners} later winner${followUpStats.laterWinners === 1 ? '' : 's'}`
              : 'No later winners'}
          </strong>
          <p>
            {followUpStats.tracked
              ? `${followUpStats.laterLosers} later loser${followUpStats.laterLosers === 1 ? '' : 's'}, ${
                  followUpStats.pending
                } pending, ${followUpStats.untracked} untracked.`
              : 'No same-symbol follow-up trades found within 72 hours.'}
          </p>
        </div>
      </div>

      {gateLearning.length > 0 && (
        <div className={styles.gateLearningPanel}>
          <div className={styles.calibrationHeader}>
            <div>
              <span>Gate Learning</span>
              <strong>What rejected outcomes say about each filter</strong>
            </div>
          </div>
          <div className={styles.gateLearningGrid}>
            {gateLearning.map((item) => (
              <button
                key={item.gate}
                type="button"
                className={`${styles.gateLearningCard} ${gateFilter === item.gate ? styles.gateLearningCardActive : ''}`}
                aria-label={`Focus ${gateLabel(item.gate)} rejects`}
                onClick={() => {
                  setGateFilter(item.gate);
                  setSelectedKey(null);
                }}
              >
                <div className={styles.gateLearningTop}>
                  <strong>{gateLabel(item.gate)}</strong>
                  <span>
                    {item.evaluated}/{item.total} evaluated
                  </span>
                </div>
                <div className={styles.gateLearningBars} aria-label={`${gateLabel(item.gate)} outcome split`}>
                  <span style={{ width: `${item.missedRate}%` }} />
                  <em style={{ width: `${item.correctRate}%` }} />
                </div>
                <div className={styles.gateLearningStats}>
                  <span>{item.missed} missed winner</span>
                  <span>{item.correct} correct reject</span>
                  <span>{item.avgReturn == null ? 'No avg yet' : `${formatPct(item.avgReturn)} avg`}</span>
                </div>
                <p>{item.action}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {calibrationSuggestions.length > 0 && (
        <div className={styles.calibrationPanel}>
          <div className={styles.calibrationHeader}>
            <div>
              <span>Scanner Calibration</span>
              <strong>Suggested adjustments from recent gate rejects</strong>
            </div>
          </div>
          <div className={styles.calibrationList}>
            {calibrationSuggestions.map((suggestion) => (
              <div key={suggestion.key} className={styles.calibrationItem}>
                <span className={`${styles.calibrationPill} ${styles[`calibration_${suggestion.severity}`]}`}>
                  {suggestion.label}
                </span>
                <div>
                  <strong>{suggestion.title}</strong>
                  <p>{suggestion.detail}</p>
                  <em>{suggestion.action}</em>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.replayShell}>
        <div className={styles.gatePanel}>
          <div className={styles.gateToolbar}>
            <span>{data.count ?? gateCandidates.length} rejects</span>
            <select value={gateFilter} onChange={(event) => setGateFilter(event.target.value)}>
              <option value="all">All gates</option>
              {gateOptions.map((gate) => (
                <option key={gate} value={gate}>
                  {gateLabel(gate)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.replayList} aria-label="Rejected candidates">
            {candidates.map((candidate) => {
              const key = `${candidate.gateLogSK}:${candidate.symbol}`;
              const active = key === `${selected.gateLogSK}:${selected.symbol}`;
              const createdLabel = formatDateTime(candidate.createdAt);
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.replayListItem} ${active ? styles.replayListItemActive : ''}`}
                  onClick={() => setSelectedKey(key)}
                >
                  <span className={styles.replayListTop}>
                    <strong>{candidate.symbol}</strong>
                    <span className={`${styles.badge} ${styles.badgeRejected}`}>{gateLabel(candidate.lastGate)}</span>
                  </span>
                  <span className={styles.replayListAction}>
                    {candidate.reason || 'Rejected without a stored reason'}
                  </span>
                  <span className={styles.replayListMeta}>
                    <span>{createdLabel}</span>
                    <span>{candidate.createdAt ? timeAgo(candidate.createdAt) : 'Time unknown'}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.replayDetail}>
          <div className={styles.replayHero}>
            <div>
              <span className={styles.replayEyebrow}>Skipped Trade Review</span>
              <h3 className={styles.replayTitle}>
                {selected.symbol} stopped at {gateLabel(selected.lastGate)}
              </h3>
              <p className={styles.replaySubtitle}>{selected.reason || 'No gate reason was recorded.'}</p>
            </div>
            <div className={styles.replayOutcome}>
              <span className={`${styles.badge} ${styles.badgeRejected}`}>Not Taken</span>
              <strong>{selected.passedGates.length} passed</strong>
            </div>
          </div>

          <div className={styles.replayMetricGrid}>
            <div className={styles.replayMetric}>
              <span>Run</span>
              <strong>{selected.runId || selected.gateLogSK.replace('STRAT_GATELOG#', '') || 'Unknown run'}</strong>
            </div>
            <div className={styles.replayMetric}>
              <span>Rejected</span>
              <strong>{formatDateTime(selected.rejectedAt || selected.createdAt)}</strong>
            </div>
            <div className={styles.replayMetric}>
              <span>Rejected Price</span>
              <strong>{selected.rejectedPrice == null ? '—' : formatMoney(selected.rejectedPrice)}</strong>
            </div>
            <div className={styles.replayMetric}>
              <span>Bias</span>
              <strong>{selected.direction || '—'}</strong>
            </div>
          </div>

          <div className={styles.capturePanel}>
            <div>
              <span className={styles.postmortemLabel}>Outcome capture</span>
              <strong>{selected.followUpStatus?.replace(/_/g, ' ') || 'Pending outcome'}</strong>
              <p>
                {selected.rejectedPrice == null
                  ? 'This older gate log does not have a rejection price yet.'
                  : `Future outcome jobs can evaluate ${selected.symbol} from ${formatMoney(selected.rejectedPrice)} after rejection.`}
              </p>
            </div>
            <div className={styles.captureMetrics}>
              <div>
                <span>Strategy</span>
                <strong>{selected.strategy || '—'}</strong>
              </div>
              <div>
                <span>Due</span>
                <strong>{selected.followUpDueAt ? formatDateTime(selected.followUpDueAt) : '—'}</strong>
              </div>
              <div>
                <span>Events</span>
                <strong>{selected.events.length}</strong>
              </div>
            </div>
          </div>

          <div className={styles.followUpPanel}>
            <div>
              <span className={styles.postmortemLabel}>Follow-up outcome</span>
              <strong
                className={styles[`followUp_${selected.followUp?.outcome || selected.followUpStatus || 'untracked'}`]}
              >
                {selected.followUp?.label || selected.followUpStatus?.replace(/_/g, ' ') || 'No follow-up'}
              </strong>
              <p>{selected.followUp?.detail || 'No follow-up data is attached to this rejection yet.'}</p>
            </div>
            <div className={styles.followUpMetrics}>
              <div>
                <span>Later trade</span>
                <strong>{selected.followUp?.action ? selected.followUp.action.replace(/_/g, ' ') : '—'}</strong>
              </div>
              <div>
                <span>Delay</span>
                <strong>
                  {selected.followUp?.hoursAfter != null ? `${selected.followUp.hoursAfter.toFixed(1)}h` : '—'}
                </strong>
              </div>
              <div>
                <span>P&L</span>
                <strong
                  className={
                    (selected.followUp?.followUpReturn1hPct ?? selected.followUp?.pnlPct ?? 0) >= 0
                      ? styles.positive
                      : styles.negative
                  }
                >
                  {selected.followUp?.followUpReturn1hPct != null
                    ? formatPct(selected.followUp.followUpReturn1hPct)
                    : selected.followUp?.pnlPct == null
                      ? '—'
                      : formatPct(selected.followUp.pnlPct)}
                </strong>
              </div>
            </div>
          </div>

          {selected.passedGates.length > 0 && (
            <div className={styles.gatePassedList}>
              <span>Passed gates</span>
              <div>
                {selected.passedGates.map((gate) => (
                  <strong key={gate}>{gateLabel(gate)}</strong>
                ))}
              </div>
            </div>
          )}

          {selected.events.length ? (
            <div className={styles.auditTimeline}>
              {selected.events.map((event, index) => (
                <div key={`${event.gate || 'gate'}:${index}`} className={styles.auditStep}>
                  <span className={styles.auditDot} />
                  <div>
                    <span className={styles.auditLabel}>
                      {gateLabel(event.gate || 'unknown gate')}
                      <em className={gateResultClass(event.result || '')}>{event.result || 'RECORDED'}</em>
                    </span>
                    <p>{event.reason || 'No reason recorded for this gate event.'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.gateEmptyTimeline}>
              <strong>No gate events recorded</strong>
              <p>This candidate was rejected, but the run did not store a step-by-step gate timeline.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Post-Mortems Tab                                               */
/* ══════════════════════════════════════════════════════════════ */

const outcomeBadgeClass = (cat: string) => {
  switch (cat) {
    case 'thesis_correct':
      return styles.badgeExecuted;
    case 'thesis_wrong':
      return styles.badgeRejected;
    case 'bad_timing':
      return styles.badgePending;
    case 'bad_sizing':
      return styles.badgePending;
    case 'external_shock':
      return styles.badgeApproval;
    case 'early_exit':
      return styles.badgeOpen;
    case 'late_exit':
      return styles.badgeOpen;
    default:
      return styles.badgePending;
  }
};

const outcomeLabel = (cat: string) => cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const RuleLibrary: React.FC<{
  rules: StrategyRuleCandidate[];
  onStatusChange: (id: string, status: StrategyRuleStatus) => void;
}> = ({ rules, onStatusChange }) => {
  const safeRules = Array.isArray(rules) ? rules.filter(isStrategyRuleCandidate) : [];
  const counts = safeRules.reduce<Record<StrategyRuleStatus, number>>(
    (acc, rule) => {
      acc[rule.status] += 1;
      return acc;
    },
    { pending: 0, active: 0, retired: 0 },
  );

  return (
    <section className={styles.ruleLibrary} aria-labelledby="rule-library-title">
      <div className={styles.ruleLibraryHeader}>
        <div>
          <span>Strategy Rules</span>
          <h3 id="rule-library-title">Rule Candidates</h3>
          <p>Promote lessons here first, then decide which ones deserve to become real guardrails.</p>
        </div>
        <div className={styles.ruleCounts} aria-label="Rule counts">
          <strong>{counts.pending}</strong>
          <span>pending</span>
          <strong>{counts.active}</strong>
          <span>active</span>
        </div>
      </div>

      {safeRules.length === 0 ? (
        <div className={styles.ruleEmpty}>Promoted inbox notes, replay lessons, and post-mortems will appear here.</div>
      ) : (
        <div className={styles.ruleList}>
          {safeRules.map((rule) => (
            <article key={rule.id} className={styles.ruleCard}>
              <div>
                <span className={styles.ruleSource}>
                  {rule.source} · {timeAgo(rule.createdAt)}
                </span>
                <p>{rule.text}</p>
                <span className={styles.ruleContext}>{rule.sourceTitle}</span>
              </div>
              <select
                className={styles.ruleStatusSelect}
                value={rule.status}
                onChange={(event) => onStatusChange(rule.id, event.target.value as StrategyRuleStatus)}
                aria-label={`Rule status for ${rule.sourceTitle}`}
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="retired">Retired</option>
              </select>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

type RuleImpact = {
  rule: StrategyRuleCandidate;
  influenced: StrategistDecision[];
  before: StrategistDecision[];
  influencedWinRate: number | null;
  influencedAvgPnl: number | null;
  beforeAvgPnl: number | null;
  delta: number | null;
  label: 'helping' | 'watch' | 'needs-data';
};

const closedWithPnl = (decisions: StrategistDecision[]) =>
  decisions.filter((decision) => decision.status !== 'open' && decision.pnlPct != null);

const avgPnl = (decisions: StrategistDecision[]): number | null => {
  const values = decisions
    .map((decision) => decision.pnlPct)
    .filter((value): value is number => typeof value === 'number');
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const winRate = (decisions: StrategistDecision[]): number | null => {
  const values = decisions.filter((decision) => decision.pnlPct != null);
  if (!values.length) return null;
  const wins = values.filter((decision) => (decision.pnlPct ?? 0) > 0).length;
  return (wins / values.length) * 100;
};

const ruleMatchesDecision = (rule: StrategyRuleCandidate, decision: StrategistDecision): boolean => {
  const snapshots = Array.isArray(decision.activeStrategyRules) ? decision.activeStrategyRules : [];
  return snapshots.some((snapshot) => snapshot.id === rule.id || snapshot.text === rule.text);
};

const computeRuleImpacts = (rules: StrategyRuleCandidate[], decisions: StrategistDecision[]): RuleImpact[] => {
  const closed = closedWithPnl(decisions);
  return rules
    .filter((rule) => rule.status === 'active')
    .map((rule) => {
      const activatedAt = new Date(rule.updatedAt || rule.createdAt).getTime();
      const influenced = closed.filter((decision) => ruleMatchesDecision(rule, decision));
      const before = Number.isNaN(activatedAt)
        ? []
        : closed.filter((decision) => new Date(decision.createdAt).getTime() < activatedAt);
      const influencedAvgPnl = avgPnl(influenced);
      const beforeAvgPnl = avgPnl(before);
      const delta = influencedAvgPnl != null && beforeAvgPnl != null ? influencedAvgPnl - beforeAvgPnl : null;
      const influencedWinRate = winRate(influenced);
      const label: RuleImpact['label'] =
        influenced.length < 3 ? 'needs-data' : delta != null && delta >= 0 ? 'helping' : 'watch';

      return {
        rule,
        influenced,
        before,
        influencedWinRate,
        influencedAvgPnl,
        beforeAvgPnl,
        delta,
        label,
      };
    })
    .sort((a, b) => {
      if (a.label !== b.label) return a.label === 'watch' ? -1 : b.label === 'watch' ? 1 : 0;
      return b.influenced.length - a.influenced.length;
    });
};

const RuleImpactPanel: React.FC<{
  rules: StrategyRuleCandidate[];
  decisions: StrategistDecision[];
}> = ({ rules, decisions }) => {
  const impacts = React.useMemo(() => computeRuleImpacts(rules, decisions), [rules, decisions]);

  if (!impacts.length) {
    return (
      <section className={styles.ruleImpactPanel} aria-labelledby="rule-impact-title">
        <div className={styles.ruleImpactHeader}>
          <span>Rule Impact</span>
          <h3 id="rule-impact-title">No active rules to measure</h3>
          <p>Activate a rule and future decision replays will start building its track record.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.ruleImpactPanel} aria-labelledby="rule-impact-title">
      <div className={styles.ruleImpactHeader}>
        <span>Rule Impact</span>
        <h3 id="rule-impact-title">Effectiveness Tracking</h3>
        <p>Measures closed trades that captured each active rule in their decision snapshot.</p>
      </div>
      <div className={styles.ruleImpactList}>
        {impacts.map((impact) => (
          <article key={impact.rule.id} className={styles.ruleImpactCard}>
            <div>
              <span className={`${styles.ruleImpactBadge} ${styles[`ruleImpact_${impact.label}`]}`}>
                {impact.label === 'needs-data' ? 'Needs data' : impact.label === 'helping' ? 'Helping' : 'Watch'}
              </span>
              <p>{impact.rule.text}</p>
              <span className={styles.ruleContext}>{impact.rule.sourceTitle}</span>
            </div>
            <div className={styles.ruleImpactMetrics}>
              <div>
                <span>Influenced</span>
                <strong>{impact.influenced.length}</strong>
              </div>
              <div>
                <span>Win rate</span>
                <strong>{impact.influencedWinRate == null ? '—' : `${impact.influencedWinRate.toFixed(0)}%`}</strong>
              </div>
              <div>
                <span>Avg P&L</span>
                <strong className={(impact.influencedAvgPnl ?? 0) >= 0 ? styles.positive : styles.negative}>
                  {impact.influencedAvgPnl == null ? '—' : formatPct(impact.influencedAvgPnl)}
                </strong>
              </div>
              <div>
                <span>Vs prior</span>
                <strong className={(impact.delta ?? 0) >= 0 ? styles.positive : styles.negative}>
                  {impact.delta == null ? '—' : `${impact.delta >= 0 ? '+' : ''}${impact.delta.toFixed(1)} pts`}
                </strong>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

const PostmortemTab: React.FC<{
  postmortems: PostMortemAnalysis[];
  decisions: StrategistDecision[];
  focusedSK?: string | null;
  ruleCandidates: StrategyRuleCandidate[];
  onPromoteRule: (draft: { text: string; source: StrategyRuleSource; sourceTitle: string }) => void;
  onRuleStatusChange: (id: string, status: StrategyRuleStatus) => void;
}> = ({ postmortems, decisions, focusedSK, ruleCandidates, onPromoteRule, onRuleStatusChange }) => {
  if (!postmortems.length) {
    return (
      <>
        <RuleImpactPanel rules={ruleCandidates} decisions={decisions} />
        <RuleLibrary rules={ruleCandidates} onStatusChange={onRuleStatusChange} />
        <div className={styles.emptyState}>
          <p>No post-mortems yet</p>
          <p style={{ fontSize: '0.85rem' }}>Post-mortems are generated automatically when positions close.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <RuleImpactPanel rules={ruleCandidates} decisions={decisions} />
      <RuleLibrary rules={ruleCandidates} onStatusChange={onRuleStatusChange} />
      {postmortems.map((pm) => (
        <div key={pm.SK} className={`${styles.decisionCard} ${pm.SK === focusedSK ? styles.focusedReviewCard : ''}`}>
          <div className={styles.decisionHeader}>
            <div>
              <span className={styles.decisionSymbol}>{pm.symbol}</span>
              <span className={styles.decisionAction}>{pm.action.replace(/_/g, ' ')}</span>
              <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {pm.strategy} / {pm.regime}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className={pm.pnlPct >= 0 ? styles.positive : styles.negative} style={{ fontWeight: 600 }}>
                {pm.pnlPct >= 0 ? '+' : ''}
                {pm.pnlPct.toFixed(1)}%
              </span>
              <span className={`${styles.badge} ${outcomeBadgeClass(pm.outcomeCategory)}`}>
                {outcomeLabel(pm.outcomeCategory)}
              </span>
            </div>
          </div>

          <div className={styles.postmortemSections}>
            <div className={styles.postmortemSection}>
              <span className={styles.postmortemLabel}>What happened</span>
              <span>{pm.whatHappened}</span>
            </div>
            {pm.signalMissed && (
              <div className={styles.postmortemSection}>
                <span className={styles.postmortemLabel}>Signal missed</span>
                <span>{pm.signalMissed}</span>
              </div>
            )}
            <div className={styles.lessonBox}>
              <span className={styles.postmortemLabel}>Lesson</span>
              <span>{pm.lesson}</span>
            </div>
            {pm.suggestedAdjustment && (
              <div className={styles.postmortemSection}>
                <span className={styles.postmortemLabel}>Suggested adjustment</span>
                <span>{pm.suggestedAdjustment}</span>
              </div>
            )}
          </div>

          <div className={styles.decisionMeta}>
            <span>{timeAgo(pm.createdAt)}</span>
            <button
              type="button"
              className={styles.ruleInlineButton}
              onClick={() =>
                onPromoteRule({
                  text: pm.suggestedAdjustment || pm.lesson,
                  source: 'Post-Mortem',
                  sourceTitle: `${pm.symbol} ${outcomeLabel(pm.outcomeCategory)}`,
                })
              }
            >
              Promote to rule
            </button>
          </div>
        </div>
      ))}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Backtest Tab                                                   */
/* ══════════════════════════════════════════════════════════════ */

const BacktestTab: React.FC<{
  result: BacktestResult | null;
  loading: boolean;
  onRun: (days: number) => void;
  optimizeResult: OptimizationResult | null;
  optimizeLoading: boolean;
  onOptimize: (days: number) => void;
  liveStats: StrategistStats | null;
}> = ({ result, loading, onRun, optimizeResult, optimizeLoading, onOptimize, liveStats }) => {
  const [days, setDays] = React.useState(90);

  return (
    <>
      <div className={styles.backtestConfig}>
        <h3 className={styles.configTitle}>Portfolio Backtest</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0 0 16px' }}>
          Simulates the strategist scanning 30 symbols across all 7 strategies with TP tiers, stops, and trailing stops.
        </p>
        <div className={styles.configGrid}>
          <div className={styles.configField}>
            <label className={styles.configLabel}>Lookback Period</label>
            <select
              title="Days"
              className={styles.configSelect}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
          <div className={styles.configField}>
            <button className={styles.runButton} onClick={() => onRun(days)} disabled={loading || optimizeLoading}>
              {loading ? 'Running...' : 'Run Backtest'}
            </button>
          </div>
          <div className={styles.configField}>
            <button
              className={styles.optimizeButton}
              onClick={() => onOptimize(days)}
              disabled={loading || optimizeLoading}
            >
              {optimizeLoading ? 'Optimizing...' : 'Optimize Params'}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className={styles.emptyState}>
          <p>Running portfolio backtest...</p>
          <p style={{ fontSize: '0.85rem' }}>
            Fetching bars for 30 symbols and simulating {days} days of trading. This may take 30-60 seconds.
          </p>
        </div>
      )}

      {!loading && result && result.metrics && (
        <>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Total Return</div>
              <div
                className={`${styles.statValue} ${result.metrics.totalReturn >= 0 ? styles.positive : styles.negative}`}
              >
                {result.metrics.totalReturn >= 0 ? '+' : ''}
                {result.metrics.totalReturn}%
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Win Rate</div>
              <div className={styles.statValue}>{result.metrics.winRate}%</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Total Trades</div>
              <div className={styles.statValue}>{result.metrics.totalTrades}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Sharpe Ratio</div>
              <div className={styles.statValue}>{result.metrics.sharpe}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Max Drawdown</div>
              <div className={`${styles.statValue} ${styles.negative}`}>{result.metrics.maxDrawdown}%</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Profit Factor</div>
              <div className={styles.statValue}>{result.metrics.profitFactor}</div>
            </div>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Avg Hold Days</div>
              <div className={styles.statValue}>{result.metrics.avgHoldDays}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Avg Win</div>
              <div className={`${styles.statValue} ${styles.positive}`}>${result.metrics.avgWin}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Avg Loss</div>
              <div className={`${styles.statValue} ${styles.negative}`}>${result.metrics.avgLoss}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>End Capital</div>
              <div className={styles.statValue}>${result.metrics.endCapital?.toLocaleString()}</div>
            </div>
          </div>

          {result.strategyBreakdown && Object.keys(result.strategyBreakdown).length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>Strategy Breakdown</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Strategy</th>
                      <th>Trades</th>
                      <th>Win Rate</th>
                      <th>P&L</th>
                      <th>Longs</th>
                      <th>Shorts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.strategyBreakdown)
                      .sort((a, b) => b[1].pnl - a[1].pnl)
                      .map(([strat, s]) => (
                        <tr key={strat}>
                          <td>
                            <strong>{strat.replace(/_/g, ' ')}</strong>
                          </td>
                          <td>{s.trades}</td>
                          <td>{s.winRate}%</td>
                          <td className={s.pnl >= 0 ? styles.positive : styles.negative}>${s.pnl.toLocaleString()}</td>
                          <td>{s.longs}</td>
                          <td>{s.shorts}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {result.equityCurve && result.equityCurve.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>Equity Curve</h3>
              <div className={styles.equityChart}>
                {(() => {
                  const eq = result.equityCurve;
                  const minEq = Math.min(...eq.map((e) => e.equity));
                  const maxEq = Math.max(...eq.map((e) => e.equity));
                  const range = maxEq - minEq || 1;
                  const startCap = result.metrics.startCapital;
                  const points = eq
                    .map((e, i) => {
                      const x = (i / (eq.length - 1)) * 100;
                      const y = 100 - ((e.equity - minEq) / range) * 100;
                      return `${x},${y}`;
                    })
                    .join(' ');
                  const baselineY = 100 - ((startCap - minEq) / range) * 100;
                  return (
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={styles.equitySvg}>
                      <line
                        x1="0"
                        y1={baselineY}
                        x2="100"
                        y2={baselineY}
                        stroke="var(--border-secondary)"
                        strokeWidth="0.3"
                        strokeDasharray="1,1"
                      />
                      <polyline
                        fill="none"
                        stroke={result.metrics.totalReturn >= 0 ? '#22c55e' : '#ef4444'}
                        strokeWidth="0.8"
                        points={points}
                      />
                    </svg>
                  );
                })()}
                <div className={styles.equityLabels}>
                  <span>{result.equityCurve[0]?.date}</span>
                  <span>{result.equityCurve[result.equityCurve.length - 1]?.date}</span>
                </div>
              </div>
            </>
          )}

          {result.trades && result.trades.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>Recent Trades ({result.trades.length})</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Direction</th>
                      <th>Strategy</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>P&L</th>
                      <th>Hold</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades
                      .slice(-50)
                      .reverse()
                      .map((t, i) => (
                        <tr key={i}>
                          <td className={styles.symbol}>{t.symbol}</td>
                          <td>{t.direction}</td>
                          <td>{(t.strategy ?? '').replace(/_/g, ' ')}</td>
                          <td>
                            ${t.entryPrice} <span className={styles.dateMuted}>{t.entryDate}</span>
                          </td>
                          <td>
                            ${t.exitPrice} <span className={styles.dateMuted}>{t.exitDate}</span>
                          </td>
                          <td className={t.pnl >= 0 ? styles.positive : styles.negative}>
                            {t.pnlPct >= 0 ? '+' : ''}
                            {t.pnlPct}%
                          </td>
                          <td>{t.holdDays}d</td>
                          <td>{t.exitReason.replace(/_/g, ' ')}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {!loading && result && liveStats && liveStats.totalTrades > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Live vs Backtest Comparison</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
            Live: last {liveStats.totalTrades} paper trades | Backtest: {result.dateRange?.start} to{' '}
            {result.dateRange?.end}
          </p>
          <div className={styles.comparisonGrid}>
            {[
              {
                label: 'Win Rate',
                live: `${liveStats.winRate}%`,
                backtest: `${result.metrics.winRate}%`,
                liveVal: liveStats.winRate,
                btVal: result.metrics.winRate,
              },
              {
                label: 'Sharpe',
                live: `${liveStats.sharpe ?? 'N/A'}`,
                backtest: `${result.metrics.sharpe}`,
                liveVal: liveStats.sharpe ?? 0,
                btVal: result.metrics.sharpe,
              },
              {
                label: 'Max Drawdown',
                live: `${liveStats.maxDrawdown ?? 'N/A'}%`,
                backtest: `${result.metrics.maxDrawdown}%`,
                liveVal: liveStats.maxDrawdown ?? 0,
                btVal: result.metrics.maxDrawdown,
              },
              {
                label: 'Avg Hold Days',
                live: `${liveStats.avgHoldDays ?? 'N/A'}`,
                backtest: `${result.metrics.avgHoldDays}`,
                liveVal: liveStats.avgHoldDays ?? 0,
                btVal: result.metrics.avgHoldDays,
              },
              {
                label: 'Total Trades',
                live: `${liveStats.totalTrades}`,
                backtest: `${result.metrics.totalTrades}`,
                liveVal: liveStats.totalTrades,
                btVal: result.metrics.totalTrades,
              },
            ].map((row) => {
              const delta = row.liveVal - row.btVal;
              const isDD = row.label === 'Max Drawdown';
              // For drawdown, closer to 0 is better (live less negative = good)
              const isGood = isDD ? delta > 0 : delta > 0;
              return (
                <div key={row.label} className={styles.comparisonCard}>
                  <h4>{row.label}</h4>
                  <div className={styles.comparisonStats}>
                    <div>
                      Live: <strong>{row.live}</strong>
                    </div>
                    <div>
                      Backtest: <strong>{row.backtest}</strong>
                    </div>
                    <div className={isGood ? styles.positive : styles.negative}>
                      {delta > 0 ? '+' : ''}
                      {typeof row.liveVal === 'number' && typeof row.btVal === 'number'
                        ? row.label.includes('%') || row.label === 'Win Rate' || row.label === 'Max Drawdown'
                          ? `${delta.toFixed(1)}`
                          : delta.toFixed(2)
                        : ''}{' '}
                      {isGood ? '\u2191' : '\u2193'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {liveStats.perStrategyBreakdown && result.strategyBreakdown && (
            <>
              <h3 className={styles.sectionTitle}>Per-Strategy: Live vs Backtest</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Strategy</th>
                      <th>Live Trades</th>
                      <th>Live WR</th>
                      <th>BT Trades</th>
                      <th>BT WR</th>
                      <th>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys({ ...liveStats.perStrategyBreakdown, ...result.strategyBreakdown }).map((strat) => {
                      const live = liveStats.perStrategyBreakdown?.[strat];
                      const bt = result.strategyBreakdown?.[strat];
                      const liveWR = live?.winRate ?? 0;
                      const btWR = bt?.winRate ?? 0;
                      const delta = liveWR - btWR;
                      return (
                        <tr key={strat}>
                          <td>
                            <strong>{strat.replace(/_/g, ' ')}</strong>
                          </td>
                          <td>{live?.trades ?? 0}</td>
                          <td>{liveWR}%</td>
                          <td>{bt?.trades ?? 0}</td>
                          <td>{btWR}%</td>
                          <td className={delta >= 0 ? styles.positive : styles.negative}>
                            {delta >= 0 ? '+' : ''}
                            {delta.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {optimizeLoading && (
        <div className={styles.emptyState}>
          <p>Optimizing parameters...</p>
          <p style={{ fontSize: '0.85rem' }}>
            Sweeping ~30 parameter combinations across {days} days. This may take 2-3 minutes.
          </p>
        </div>
      )}

      {!optimizeLoading && optimizeResult && (
        <>
          <h3 className={styles.sectionTitle}>
            Optimization Results ({optimizeResult.runsCompleted} runs in {optimizeResult.elapsed}s)
          </h3>

          <div className={styles.comparisonGrid}>
            <div className={styles.comparisonCard}>
              <h4>Default Config</h4>
              <div className={styles.comparisonStats}>
                <div>
                  Return:{' '}
                  <span className={optimizeResult.defaultMetrics.totalReturn >= 0 ? styles.positive : styles.negative}>
                    {optimizeResult.defaultMetrics.totalReturn}%
                  </span>
                </div>
                <div>Sharpe: {optimizeResult.defaultMetrics.sharpe}</div>
                <div>Win Rate: {optimizeResult.defaultMetrics.winRate}%</div>
                <div>Max DD: {optimizeResult.defaultMetrics.maxDrawdown}%</div>
                <div>Trades: {optimizeResult.defaultMetrics.totalTrades}</div>
              </div>
            </div>
            <div className={`${styles.comparisonCard} ${styles.optimizedCard}`}>
              <h4>Optimized Config</h4>
              <div className={styles.comparisonStats}>
                <div>
                  Return:{' '}
                  <span
                    className={optimizeResult.optimizedMetrics.totalReturn >= 0 ? styles.positive : styles.negative}
                  >
                    {optimizeResult.optimizedMetrics.totalReturn}%
                  </span>
                </div>
                <div>Sharpe: {optimizeResult.optimizedMetrics.sharpe}</div>
                <div>Win Rate: {optimizeResult.optimizedMetrics.winRate}%</div>
                <div>Max DD: {optimizeResult.optimizedMetrics.maxDrawdown}%</div>
                <div>Trades: {optimizeResult.optimizedMetrics.totalTrades}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <h3 className={styles.sectionTitle}>Best Parameters</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(optimizeResult.bestConfig).map(([k, v]) => (
                      <tr key={k}>
                        <td>{k.replace(/_/g, ' ')}</td>
                        <td>
                          <strong>{typeof v === 'number' ? v.toFixed(1) : v}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: '280px' }}>
              <h3 className={styles.sectionTitle}>Validation Split</h3>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>In-Sample (train)</td>
                      <td>
                        {optimizeResult.inSampleRange.start} to {optimizeResult.inSampleRange.end}
                      </td>
                    </tr>
                    <tr>
                      <td>Out-of-Sample (test)</td>
                      <td>
                        {optimizeResult.outOfSampleRange.start} to {optimizeResult.outOfSampleRange.end}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {optimizeResult.paramSensitivity &&
            optimizeResult.paramSensitivity.length > 0 &&
            (() => {
              // Group sensitivity data by parameter
              const grouped: Record<
                string,
                Array<{ value: number; sharpe: number; winRate: number; totalReturn: number; trades: number }>
              > = {};
              for (const s of optimizeResult.paramSensitivity) {
                if (!grouped[s.param]) grouped[s.param] = [];
                grouped[s.param].push(s);
              }
              return (
                <>
                  <h3 className={styles.sectionTitle}>Parameter Sensitivity</h3>
                  <div className={styles.sensitivityGrid}>
                    {Object.entries(grouped).map(([param, entries]) => {
                      const maxSharpe = Math.max(...entries.map((e) => Math.abs(e.sharpe)), 0.01);
                      const maxReturn = Math.max(...entries.map((e) => Math.abs(e.totalReturn)), 0.01);
                      const bestIdx = entries.reduce((bi, e, i) => (e.sharpe > entries[bi].sharpe ? i : bi), 0);
                      return (
                        <div key={param} className={styles.sensitivityCard}>
                          <h4 className={styles.sensitivityTitle}>{param.replace(/_/g, ' ')}</h4>
                          <div className={styles.chartArea}>
                            {entries.map((e, i) => {
                              const sharpeWidth = Math.max(2, (Math.abs(e.sharpe) / maxSharpe) * 100);
                              const returnWidth = Math.max(2, (Math.abs(e.totalReturn) / maxReturn) * 100);
                              const isBest = i === bestIdx;
                              return (
                                <div key={i} className={`${styles.chartRow} ${isBest ? styles.chartRowBest : ''}`}>
                                  <div className={styles.chartLabel}>{e.value}</div>
                                  <div className={styles.chartBars}>
                                    <div className={styles.barGroup}>
                                      <div
                                        className={`${styles.bar} ${e.sharpe >= 0 ? styles.barSharpe : styles.barNeg}`}
                                        style={{ width: `${sharpeWidth}%` }}
                                      />
                                      <span className={styles.barValue}>{e.sharpe.toFixed(2)}</span>
                                    </div>
                                    <div className={styles.barGroup}>
                                      <div
                                        className={`${styles.bar} ${e.totalReturn >= 0 ? styles.barReturn : styles.barNeg}`}
                                        style={{ width: `${returnWidth}%` }}
                                      />
                                      <span className={styles.barValue}>{e.totalReturn}%</span>
                                    </div>
                                  </div>
                                  <div className={styles.chartMeta}>{e.winRate}% WR</div>
                                </div>
                              );
                            })}
                          </div>
                          <div className={styles.chartLegend}>
                            <span>
                              <span className={styles.legendDot} style={{ background: '#6366f1' }} /> Sharpe
                            </span>
                            <span>
                              <span className={styles.legendDot} style={{ background: '#22c55e' }} /> Return
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
        </>
      )}

      {!loading && !optimizeLoading && !result && !optimizeResult && (
        <div className={styles.emptyState}>
          <p>No backtest results yet</p>
          <p style={{ fontSize: '0.85rem' }}>
            Click "Run Backtest" for a single run, or "Optimize Params" to find the best configuration.
          </p>
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Weekly Reviews Tab                                            */
/* ══════════════════════════════════════════════════════════════ */

const WeeklyTab: React.FC<{ reviews: WeeklyReview[]; runs: StrategistRun[] }> = ({ reviews, runs }) => {
  return (
    <>
      {runs.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Recent Runs</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Symbols</th>
                  <th>Actions</th>
                  <th>Market Assessment</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.SK}>
                    <td>{timeAgo(r.createdAt)}</td>
                    <td>{r.symbolsScanned}</td>
                    <td>{r.actionsEmitted}</td>
                    <td className={styles.runAssessment}>{r.marketAssessment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 className={styles.sectionTitle}>Weekly Self-Reviews</h3>
      {!reviews.length ? (
        <div className={styles.emptyState}>
          <p>No weekly reviews yet</p>
          <p style={{ fontSize: '0.85rem' }}>Reviews are generated every Sunday evening.</p>
        </div>
      ) : (
        reviews.map((r) => (
          <div key={r.SK} className={styles.reviewCard}>
            <h3 className={styles.reviewTitle}>Week of {r.SK.replace('STRAT_WEEKLY#', '')}</h3>
            <div className={styles.reviewMeta}>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Win Rate</span>
                <span className={styles.reviewStatValue}>{r.trackRecord.winRate}%</span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Avg P&L</span>
                <span
                  className={`${styles.reviewStatValue} ${r.trackRecord.avgPnlPct >= 0 ? styles.positive : styles.negative}`}
                >
                  {r.trackRecord.avgPnlPct >= 0 ? '+' : ''}
                  {r.trackRecord.avgPnlPct}%
                </span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Trades</span>
                <span className={styles.reviewStatValue}>{r.trackRecord.totalTrades}</span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Long Bias</span>
                <span className={`${styles.biasTag} ${biasClass(r.longBiasAdj)}`}>{r.longBiasAdj}</span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Short Bias</span>
                <span className={`${styles.biasTag} ${biasClass(r.shortBiasAdj)}`}>{r.shortBiasAdj}</span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Risk</span>
                <span
                  className={`${styles.biasTag} ${biasClass(r.riskAdj === 'more_conservative' ? 'decrease' : r.riskAdj === 'more_aggressive' ? 'increase' : 'maintain')}`}
                >
                  {r.riskAdj.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
            <p className={styles.reviewSummary}>{r.summary}</p>
            {r.sectorNotes && (
              <p className={styles.reviewSectors}>
                <strong>Sectors:</strong> {r.sectorNotes}
              </p>
            )}
            {r.keyLesson && (
              <div className={styles.lessonBox}>
                <span className={styles.lessonLabel}>Key Lesson:</span>
                {r.keyLesson}
              </div>
            )}
          </div>
        ))
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* Monthly Evolution Tab                                         */
/* ══════════════════════════════════════════════════════════════ */

const MonthlyTab: React.FC<{ reviews: any[] }> = ({ reviews }) => {
  return (
    <>
      <h3 className={styles.sectionTitle}>Monthly Strategy Evolution</h3>
      {!reviews.length ? (
        <div className={styles.emptyState}>
          <p>No monthly reviews yet</p>
          <p style={{ fontSize: '0.85rem' }}>Monthly evolution reviews run on the 1st of each month.</p>
        </div>
      ) : (
        reviews.map((r: any) => (
          <div key={r.SK} className={styles.reviewCard}>
            <h3 className={styles.reviewTitle}>Month of {r.SK?.replace('STRAT_MONTHLY#', '') || '?'}</h3>
            <div className={styles.reviewMeta}>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Win Rate</span>
                <span className={styles.reviewStatValue}>{r.trackRecord?.winRate ?? '—'}%</span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Avg P&L</span>
                <span
                  className={`${styles.reviewStatValue} ${(r.trackRecord?.avgPnlPct ?? 0) >= 0 ? styles.positive : styles.negative}`}
                >
                  {(r.trackRecord?.avgPnlPct ?? 0) >= 0 ? '+' : ''}
                  {r.trackRecord?.avgPnlPct ?? 0}%
                </span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Trades</span>
                <span className={styles.reviewStatValue}>{r.trackRecord?.totalTrades ?? '—'}</span>
              </div>
              <div className={styles.reviewStat}>
                <span className={styles.reviewStatLabel}>Risk</span>
                <span
                  className={`${styles.biasTag} ${biasClass(r.riskPosture === 'more_conservative' ? 'decrease' : r.riskPosture === 'more_aggressive' ? 'increase' : 'maintain')}`}
                >
                  {(r.riskPosture || 'maintain').replace(/_/g, ' ')}
                </span>
              </div>
              {r.confidenceAdjustment !== 0 && (
                <div className={styles.reviewStat}>
                  <span className={styles.reviewStatLabel}>Confidence Adj</span>
                  <span className={styles.reviewStatValue}>
                    {r.confidenceAdjustment > 0 ? '+' : ''}
                    {r.confidenceAdjustment}
                  </span>
                </div>
              )}
              {r.maxPositionsAdj !== 0 && (
                <div className={styles.reviewStat}>
                  <span className={styles.reviewStatLabel}>Max Positions Adj</span>
                  <span className={styles.reviewStatValue}>
                    {r.maxPositionsAdj > 0 ? '+' : ''}
                    {r.maxPositionsAdj}
                  </span>
                </div>
              )}
            </div>
            <p className={styles.reviewSummary}>{r.summary}</p>

            {/* Strategy Rotation */}
            {r.strategyRotation && (
              <div style={{ margin: '10px 0', fontSize: '0.85rem' }}>
                <strong>Strategy Rotation:</strong>
                {r.strategyRotation.favor?.length > 0 && (
                  <span style={{ color: 'var(--color-positive)', marginLeft: 8 }}>
                    Favor: {r.strategyRotation.favor.join(', ')}
                  </span>
                )}
                {r.strategyRotation.reduce?.length > 0 && (
                  <span style={{ color: 'var(--color-negative)', marginLeft: 8 }}>
                    Reduce: {r.strategyRotation.reduce.join(', ')}
                  </span>
                )}
                {r.strategyRotation.disable?.length > 0 && (
                  <span style={{ color: '#ef4444', marginLeft: 8 }}>
                    Disable: {r.strategyRotation.disable.join(', ')}
                  </span>
                )}
              </div>
            )}

            {/* Sector Rotation */}
            {r.sectorRotation && (
              <div style={{ margin: '10px 0', fontSize: '0.85rem' }}>
                <strong>Sector Rotation:</strong>
                {r.sectorRotation.overweight?.length > 0 && (
                  <span style={{ color: 'var(--color-positive)', marginLeft: 8 }}>
                    Overweight: {r.sectorRotation.overweight.join(', ')}
                  </span>
                )}
                {r.sectorRotation.underweight?.length > 0 && (
                  <span style={{ color: 'var(--color-negative)', marginLeft: 8 }}>
                    Underweight: {r.sectorRotation.underweight.join(', ')}
                  </span>
                )}
                {r.sectorRotation.avoid?.length > 0 && (
                  <span style={{ color: '#ef4444', marginLeft: 8 }}>Avoid: {r.sectorRotation.avoid.join(', ')}</span>
                )}
              </div>
            )}

            {r.keyEvolution && (
              <div className={styles.lessonBox}>
                <span className={styles.lessonLabel}>Key Evolution:</span>
                {r.keyEvolution}
              </div>
            )}
          </div>
        ))
      )}
    </>
  );
};

export default Strategist;
